import React, { Suspense, useRef, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, TrackballControls, Environment, Grid, ContactShadows, useGLTF, useProgress, Html, TransformControls, Line } from '@react-three/drei';
import { useSpring } from '@react-spring/three';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import Icon from '../../../components/AppIcon';

const extractionCache = new Map();

const XOR_KEY = 0x4F; // Symmetrical XOR decryption Key to unlock scrambled files

function deobfuscateBuffer(arrayBuffer) {
  const view = new Uint8Array(arrayBuffer);
  const limit = Math.min(view.length, 100);
  for (let i = 0; i < limit; i++) {
    view[i] = view[i] ^ XOR_KEY;
  }
  return arrayBuffer;
}

const NOOP = () => {};

export function cleanMeshName(str) {
  if (!str) return '';
  return str.replace(/[^a-zA-Z0-9]/g, '').toLowerCase().replace(/\d+$/, '');
}

export function checkNodeIsHidden(node, visibilityMap, componentWraps) {
  if (!node) return false;
  const nodeName = node.name || '';
  const nodeClean = cleanMeshName(nodeName);
  const componentIds = node.userData.componentIds || [];

  // Swap / Wrap custom hidden state check
  if (componentWraps) {
    for (const key of [nodeName, ...componentIds]) {
      if (key && componentWraps[key]?.hidden) return true;
    }
  }

  if (!visibilityMap || Object.keys(visibilityMap).length === 0) return false;

  // 1. Direct ID & Name checks
  for (const id of componentIds) {
    if (visibilityMap[id] === false) return true;
  }
  if (visibilityMap[nodeName] === false || visibilityMap[nodeName.toLowerCase()] === false) {
    return true;
  }
  if (node.uuid && visibilityMap[node.uuid] === false) {
    return true;
  }

  // 2. Clean name match against any false visibility keys
  if (nodeClean) {
    for (const [key, isVis] of Object.entries(visibilityMap)) {
      if (isVis === false) {
        const kClean = cleanMeshName(key);
        if (kClean && (kClean === nodeClean || nodeClean.includes(kClean) || kClean.includes(nodeClean))) {
          return true;
        }
      }
    }
  }

  return false;
}

const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('/draco/');
useGLTF.setDecoderPath('/draco/');

// Symmetrical Dynamic Texture Decal Cacher
const textureCache = new Map();
const getCachedTexture = (url, onLoad) => {
  if (textureCache.has(url)) {
    return textureCache.get(url);
  }
  const loader = new THREE.TextureLoader();
  const tex = loader.load(url, (loadedTex) => {
    loadedTex.wrapS = THREE.RepeatWrapping;
    loadedTex.wrapT = THREE.RepeatWrapping;
    loadedTex.needsUpdate = true;
    onLoad?.();
  });
  textureCache.set(url, tex);
  return tex;
};

// Symmetrical Shared Materials optimization:
// Pre-compiling static highlight and x-ray materials globally completely eliminates
// on-the-fly material clones and GPU shader compilation spikes during selection clicks,
// fully preventing mobile browser resets and page crashes!
const sharedHighlightMaterial = new THREE.MeshBasicMaterial({
  color: new THREE.Color('#22c55e'), // Glowing emerald green
});

const sharedXrayMaterial = new THREE.MeshStandardMaterial({
  color: new THREE.Color('#94a3b8'), // Sleek, modern slate-400 tint
  transparent: true,
  opacity: 0.25, // Frosted translucent silhouette
  roughness: 0.8,
  metalness: 0.1,
  depthWrite: false,
});

const GltfSceneResolver = ({ modelUrl, onGltfResolved }) => {
  const gltf = useGLTF(modelUrl, true);
  const callbackRef = useRef(onGltfResolved);
  
  useEffect(() => {
    callbackRef.current = onGltfResolved;
  });

  useEffect(() => {
    if (gltf) {
      callbackRef.current(gltf);
    }
  }, [gltf]);
  return null;
};

const SubbedGlbModel = ({ 
  compId, 
  wrap, 
  onUpdate, 
  isSelected, 
  gizmoMode,
  onClick, // Symmetrical: Receive click triggers
  onDoubleClick, // Symmetrical: Receive double click triggers
  gizmoSize = 0.8, // Symmetrical: Receive active transform gizmo handle size!
  onGizmoSizeChange = NOOP // Symmetrical: Receive active gizmo size scale modifier callback!
}) => {
  // Symmetrical Multi-Option URL Resolver:
  // Dynamically loads the GLB model corresponding to the activeOptionIndex of the swapOptions array!
  // Falls back natively to standard subbedGlbUrl for backward compatibility.
  const activeUrl = wrap.swapOptions && wrap.swapOptions.length > 0 
    ? (wrap.swapOptions[wrap.activeOptionIndex ?? 0]?.glbUrl || wrap.subbedGlbUrl)
    : wrap.subbedGlbUrl;

  const gltf = useGLTF(activeUrl, true);
  const meshRef = useRef();
  const transformRef = useRef(); // Symmetrical Ref: Accesses underlying ThreeJS TransformControls helper axes!

  // Symmetrical Scene Cloner:
  // Creates an independent, isolated Object3D clone of the cached GLTF scene.
  // This allows rendering multiple concurrent instances of the same model (like four wheels) concurrently!
  const clone = useMemo(() => {
    if (gltf?.scene) {
      const cloned = gltf.scene.clone();
      cloned.name = compId; // Assign parent name to the root cloned node
      
      // Symmetrically recursively clone all materials to guarantee 100% decoupling!
      cloned.traverse(node => {
        if (node.isMesh && node.material) {
          node.material = Array.isArray(node.material) 
            ? node.material.map(m => m.clone()) 
            : node.material.clone();
        }
      });
      return cloned;
    }
    return null;
  }, [gltf?.scene, compId]);

  // Symmetrical Target Mesh State: Holds the actual, fully-resolved ThreeJS Group Object3D!
  const [targetMesh, setTargetMesh] = useState(null);
  const [isDragging, setIsDragging] = useState(false); // Symmetrical: Track live dragging status to prevent re-render collisions!

  // Symmetrical Coordinate Sync: Imperatively apply coordinates ONLY when NOT actively dragging!
  // Both standard models and aftermarket custom GLBs are rendered natively at standard 1:1 scale!
  useEffect(() => {
    if (meshRef.current && !isDragging) {
      meshRef.current.position.set(
        wrap.position?.[0] ?? 0,
        wrap.position?.[1] ?? 0,
        wrap.position?.[2] ?? 0
      );
      meshRef.current.rotation.set(
        wrap.rotation?.[0] ?? 0,
        wrap.rotation?.[1] ?? 0,
        wrap.rotation?.[2] ?? 0
      );
      meshRef.current.scale.set(
        wrap.scale?.[0] ?? 1,
        wrap.scale?.[1] ?? 1,
        wrap.scale?.[2] ?? 1
      );
    }
  }, [clone, wrap.position, wrap.rotation, wrap.scale, isDragging]);

  // Symmetrical Drei Controls Sync: Retrieve active default camera OrbitControls using useThree!
  const { controls } = useThree();

  // Color, material, and selection-pulse mapping for subbed GLB mesh
  // We traverse the persistent clone directly instead of meshRef to guarantee success on first frame mount!
  useEffect(() => {
    if (clone) {
      clone.traverse(node => {
        if (node.isMesh) {
          // Symmetrical pulse selector link: Assign parent compId to child mesh userData componentIds!
          // This allows parent group traversers to instantly detect selection and pulse the materials seamlessly!
          node.componentIds = [compId]; // Symmetrical list assignment!
          node.userData.componentIds = [compId];
          node.userData.isSubbedGlb = true; // Symmetrical: Mark as custom subbed GLB mesh to bypass background base visibility overrides!

          if (wrap.color) {
            node.material = node.material.clone();
            node.material.color.set(wrap.color);
          }
        }
      });
    }
  }, [clone, wrap.color, compId]);

  // Symmetrical Gizmo Depth Overrides:
  // Traverses underlying TransformControls handle meshes and disables Depth Testing!
  // This forces WebGL to always draw axes arrows on top of everything, completely preventing closeup mesh clipping!
  useEffect(() => {
    if (transformRef.current) {
      transformRef.current.traverse((node) => {
        if (node.isMesh && node.material) {
          node.material.depthTest = false;
          node.material.depthWrite = false;
          node.material.transparent = true;
          node.material.opacity = 0.9;
        }
      });
    }
  }, [targetMesh, isSelected, gizmoMode]);

  // Symmetrical R3F Group wrapper: Allows reliable pointer event capturing and raycasting select!
  // Note: We omit position/rotation/scale props from JSX here so R3F never overwrites our WebGL coordinates in ThreeJS!
  const modelNode = (
    <group 
      ref={(node) => {
        meshRef.current = node;
        if (node && targetMesh !== node) {
          setTargetMesh(node); // Symmetrical: Save active group mesh instance in state!
        }
      }}
      name={compId} // Symmetrical: Assign name to the group node so raycasters can resolve selection!
      onClick={(e) => {
        e.stopPropagation(); // Stop click propagation to canvas!
        if (onClick) {
          onClick({ id: compId, name: wrap.componentName || compId }, e.ctrlKey || e.metaKey);
        }
      }} 
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (onDoubleClick) {
          onDoubleClick({ id: compId, name: wrap.componentName || compId });
        }
      }}
    >
      {clone && (
        <primitive 
          object={clone} 
        />
      )}
    </group>
  );

  return (
    <>
      {targetMesh && isSelected && (
        <TransformControls 
          ref={transformRef} // Symmetrical: Attach the handle axes ref cleanly!
          object={targetMesh} // Symmetrical: Always bound to stateful targetMesh when selected!
          mode={gizmoMode} 
          size={gizmoSize} // Symmetrical: Set handle size dynamically so it never clips closeups!
          enabled={isSelected && !!gizmoMode} // Symmetrical: Control activation state instead of unmounting completely!
          visible={isSelected && !!gizmoMode} // Symmetrical: Hide visual gizmo arrows when not selected!
          onObjectChange={() => {
            // Symmetrical Live Coordinates HUD Sync:
            // Write coordinates continuously to parent state during drags so numbers change live in real-time!
            // We remove the async isDragging closure check to allow immediate continuous state updates!
            if (targetMesh && isSelected) {
              const pos = [
                targetMesh.position.x,
                targetMesh.position.y,
                targetMesh.position.z
              ];
              const rot = [
                targetMesh.rotation.x,
                targetMesh.rotation.y,
                targetMesh.rotation.z
              ];
              const scl = [
                targetMesh.scale.x,
                targetMesh.scale.y,
                targetMesh.scale.z
              ];

              onUpdate(compId, {
                position: pos,
                rotation: rot,
                scale: scl
              });
            }
          }}
          onDraggingChange={(e) => {
            // Detect dragging status dynamically supporting both Drei and Three event signatures
            const dragging = e?.value !== undefined ? e.value : (e?.target?.dragging ?? false);

            setIsDragging(dragging); // Sync local dragging status!

            // Symmetrical Memory Lock: Toggle OrbitControls directly in memory with ZERO react re-renders!
            if (controls) {
              controls.enabled = !dragging;
            }

            // Symmetrical Release Persistence Sync: 
            // Update parent state coordinates ONLY when dragging finishes (dragging === false)!
            if (!dragging && targetMesh && isSelected) {
              const pos = [
                targetMesh.position.x,
                targetMesh.position.y,
                targetMesh.position.z
              ];
              const rot = [
                targetMesh.rotation.x,
                targetMesh.rotation.y,
                targetMesh.rotation.z
              ];
              const scl = [
                targetMesh.scale.x,
                targetMesh.scale.y,
                targetMesh.scale.z
              ];

              console.log('🎯 Swaps dragging released! Target Pos:', pos);

              onUpdate(compId, {
                position: pos,
                rotation: rot,
                scale: scl
              });
            }
          }}
        />
      )}
      {modelNode}
    </>
  );
};

const LoadedModel = ({
  modelUrl,
  isChunked: isChunkedProp,
  controlsRef,
  setCameraPosition,
  isMini = false,
  setFloorY,
  lighting,
  selectedComponent,
  zRayComponentIds = [],
  viewMode,
  onComponentsExtracted = NOOP,
  componentVisibility = {},
  onComponentClick = NOOP,
  setCanvasDraggable,
  allowDragToInvoice,
  onContextMenuRequest = NOOP, 
  isPlaying,  
  componentColors = {}, 
  isExploded, 
  explodeScaleX,
  explodeScaleY,
  explodeScaleZ,
  activeMeasurementTool,
  measurements,
  measurementPoints = [],
  onAddMeasurementPoint,
  onCompleteArea,
  onCompletePerimeter,
  selectedMeasurementId,
  selectedPointKey,
  onSelectPointKey,
  unit = 'mm',
  chunkProgress,
  setChunkProgress,
  onComponentDoubleClick,
  isInfiniteVertical = false,
  diagnosticRecords = {},
  componentWraps = {},
  modelYShift = 0,
  isMultiSelectActive = false,
  selectedComponentIds = [],
  onUpdateComponentWrap = NOOP,
  gizmoMode = 'translate',
  onToggleOrbit, // Symmetrical: Receive OrbitControls toggle callback!
  gizmoSize = 0.8, // Symmetrical: Receive active transform gizmo handle size!
  onGizmoSizeChange = NOOP, // Symmetrical: Receive active gizmo size updater!
  repairGuideOffsets = {},
  repairBuilderActive = false,
  onRepairPartMoved = NOOP,
  activeStepIndex = 0,
  activeStepCamera = null,
  showStringLines = false,
  activeStepActions = []
}) => {
  const groupRef = useRef();
  const lerpTargetRef = useRef(new THREE.Vector3(0, isMini ? 0.45 : 0, 0)); // Symmetrical Cinematic Focus: Target Vector3 to lerp OrbitControls pivot target smoothly!
  const isSlidingRef = useRef(false); // Symmetrical sliding focus lock: Releases panning automatically once gliding settles!
  const { camera, scene, controls } = useThree();

  const transitionStartTimeRef = useRef(0);
  const prevActiveStepIndexRef = useRef(activeStepIndex);

  useEffect(() => {
    // Symmetrical high-fidelity reset: trigger transition animation whenever step index changes!
    transitionStartTimeRef.current = -1; // Flag to reset on next frame loop
  }, [activeStepIndex]);

  const cameraLerpTargetRef = useRef(null);
  const cameraLerpPosRef = useRef(null);
  const cameraLerpStartTimeRef = useRef(0);
  const prevStepIndexRef = useRef(activeStepIndex);

  useEffect(() => {
    // Only glide camera if we explicitly transition steps (activeStepIndex changed)!
    // This frees up OrbitControls completely during drags and edits!
    if (activeStepCamera && activeStepIndex !== prevStepIndexRef.current) {
      prevStepIndexRef.current = activeStepIndex;
      
      if (activeStepCamera.position && activeStepCamera.position[0] !== null) {
        cameraLerpTargetRef.current = new THREE.Vector3(...activeStepCamera.target);
        cameraLerpPosRef.current = new THREE.Vector3(...activeStepCamera.position);
        cameraLerpStartTimeRef.current = -1; // Reset transition start time
      }
    } else if (activeStepIndex !== prevStepIndexRef.current) {
      // Step changed but has no camera angle, reset refs
      prevStepIndexRef.current = activeStepIndex;
      cameraLerpTargetRef.current = null;
      cameraLerpPosRef.current = null;
    }
  }, [activeStepCamera, activeStepIndex]);

  // Symmetrical camera capture controller listener
  useEffect(() => {
    const handleRequestCapture = () => {
      if (camera && controlsRef?.current) {
        window.dispatchEvent(new CustomEvent('camera-captured', {
          detail: {
            position: camera.position.clone(),
            target: controlsRef.current.target.clone()
          }
        }));
      }
    };

    window.addEventListener('request-camera-capture', handleRequestCapture);
    return () => {
      window.removeEventListener('request-camera-capture', handleRequestCapture);
    };
  }, [camera, controlsRef]);

  // Symmetrical: Listen for manual camera preview triggers (from authoring dashboard clicks)
  useEffect(() => {
    const handleApplyStepCamera = (e) => {
      const angle = e.detail;
      if (angle && angle.position && angle.position[0] !== null) {
        cameraLerpTargetRef.current = new THREE.Vector3(...angle.target);
        cameraLerpPosRef.current = new THREE.Vector3(...angle.position);
        cameraLerpStartTimeRef.current = -1; // Reset transition start time
      }
    };

    window.addEventListener('apply-step-camera', handleApplyStepCamera);
    return () => {
      window.removeEventListener('apply-step-camera', handleApplyStepCamera);
    };
  }, []);

  // Symmetrical cached coordinates for 100% aligned reset views
  const initialPosRef = useRef(new THREE.Vector3());
  const initialQuatRef = useRef(new THREE.Quaternion());
  const initialTargetRef = useRef(new THREE.Vector3());
  const loadedUrlRef = useRef(null); // Ref-guard to prevent double loads in React StrictMode!
  const hasExtractedRef = useRef(null); // Ref-guard to prevent infinite components extraction re-render cascades!

  // Symmetrical: Find and track the 3D mesh node corresponding to selectedComponent for guide translation dragging!
  const [builderTargetMesh, setBuilderTargetMesh] = useState(null);

  useEffect(() => {
    if (repairBuilderActive && selectedComponent && groupRef.current) {
      let found = null;
      const selectNameLower = selectedComponent.name?.toLowerCase() || '';
      
      groupRef.current.traverse((node) => {
        if (!found && (node.isMesh || node.isGroup)) {
          if (node.name && node.name.toLowerCase() === selectNameLower) {
            found = node;
          }
        }
      });
      setBuilderTargetMesh(found);
    } else {
      setBuilderTargetMesh(null);
    }
  }, [selectedComponent, repairBuilderActive]);

  // Symmetrical Scene States supporting both standard monolithic GLB and secure chunked assemblies
  const [activeScene, setActiveScene] = useState(null);
  const [modelCenter, setModelCenter] = useState(new THREE.Vector3(0, 0, 0));
  const [highlightedWorldPos, setHighlightedWorldPos] = useState(null);
  const [isCentered, setIsCentered] = useState(false);
  const [isolatedCount, setIsolatedCount] = useState(0);
  const isFinalCenteredRef = useRef(false);
  const hasIsolatedRef = useRef(null);

  // Synchronous format check: Evaluates immediately on mount to prevent standard loaders from fetching manifests!
  const cleanUrl = modelUrl ? modelUrl.split('?')[0] : '';
  const isChunked = isChunkedProp === true || cleanUrl.endsWith('.json') || cleanUrl.endsWith('manifest.gltf') || cleanUrl.includes('/manifest.gltf') || modelUrl.includes('manifest') || modelUrl.includes('.json');

  const modelCameras = activeScene?.userData?.cameras || [];
  const hasEmbeddedCamera = isChunked || (modelCameras && modelCameras.length > 0);

  // Symmetrically recursively clone all materials inside activeScene to guarantee 100% decoupling from shared global cache!
  // Skip this for chunked models, as chunks already parse unique material instances natively on load, preventing performance lag!
  useEffect(() => {
    if (activeScene && !isChunked) {
      activeScene.traverse(node => {
        if (node.isMesh && node.material && !node.userData.hasClonedMaterial) {
          const clonedMats = Array.isArray(node.material) 
            ? node.material.map(m => m.clone()) 
            : node.material.clone();
          node.userData.originalMaterial = clonedMats; // Symmetrically cache cloned isolated material!
          node.material = clonedMats;
          node.userData.hasClonedMaterial = true;
        }
      });
    }
  }, [activeScene, chunkProgress, isChunked]);

  // This effect fetches, decodes, and reassembles secure chunked models asynchronously
  useEffect(() => {
    if (!modelUrl || !modelUrl.startsWith('http')) return;

    if (loadedUrlRef.current === modelUrl) {
      // Already loading/loaded this model URL! Bypasses restarts on React StrictMode or URL re-renders!
      return;
    }
    loadedUrlRef.current = modelUrl;

    setIsCentered(false);
    isFinalCenteredRef.current = false;

    if (isChunked) {
      console.log('📦 R3F Tracing Audit - CHUNKED ASSEMBLER RUNNING for:', modelUrl);
      setChunkProgress(0);
      setActiveScene(null);

      // Symmetrical active cancellation token to prevent concurrent loads in StrictMode
      let active = true;

      const assembleModel = async () => {
        try {
          const res = await fetch(modelUrl);
          if (!active) return;
          if (!res.ok) {
            throw new Error(`Failed to fetch model manifest from storage. Status: ${res.status} (${res.statusText})`);
          }
          const manifest = await res.json();
          if (!active) return;

          const group = new THREE.Group();
          group.name = '3D Model';
          
          const baseFolder = modelUrl.substring(0, modelUrl.lastIndexOf('/') + 1);
          let loadedCount = 0;
          const pendingNodes = [];
          const extractedList = [];
          const materialCache = new Map();

          // Symmetrical Adaptive Worker Pool:
          // Detect device form-factor to dynamically throttle concurrency.
          // On mobile, cap parallel workers at 1 (fully sequential) to guarantee 100% crash-free loading 
          // on extremely large assemblies in iOS Safari/Chrome, while keeping our event-loop yielding active.
          // On desktop, cap parallel workers at 4 to saturate network threads without triggering CDN 429 rate-limiting!
          const isMobile = window.innerWidth < 768 || /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
          const concurrencyLimit = isMobile ? 1 : 4;

          // Symmetrical Real-Time Materialization:
          // Immediately set the reassembled group as the active scene!
          // R3F will mount it instantly on both mobile and desktop, letting
          // components pop/materialize into the viewport in real-time as each chunk loads!
          setActiveScene(group);
          
          // Extract existing cache version from modelUrl query params to align with parent rendering cache!
          let urlVersion = '';
          try {
            const urlObj = new URL(modelUrl);
            urlVersion = urlObj.searchParams.get('v') || urlObj.searchParams.get('version') || '';
          } catch (e) {
            if (modelUrl.includes('?')) {
              const params = new URLSearchParams(modelUrl.substring(modelUrl.indexOf('?')));
              urlVersion = params.get('v') || params.get('version') || '';
            }
          }
          const loadSessionId = urlVersion || Date.now();
          let taskQueue = [...manifest.hierarchy];
          if (isMini && selectedComponent) {
            const selectId = selectedComponent.globalComponentId || 
                            selectedComponent.componentId || 
                            selectedComponent.component_id || 
                            selectedComponent.component?.id ||
                            selectedComponent.dbComponentId ||
                            selectedComponent.id;

            taskQueue = taskQueue.filter(item => {
              const itemCompIds = (item.componentIds || []).map(id => id.toString().toLowerCase());
              const matchId = selectId.toString().toLowerCase();
              return itemCompIds.includes(matchId);
            });

            // Symmetrical Fallback: Name matching with cleaned name (removing "for ...")
            if (taskQueue.length === 0 && selectedComponent.name) {
              const cleanName = selectedComponent.name.split(' for ')[0].trim().toLowerCase();
              taskQueue = [...manifest.hierarchy].filter(item => {
                const itemName = (item.name || '').toLowerCase();
                return itemName.includes(cleanName) || cleanName.includes(itemName);
              });
            }

            // Symmetrical Fuzzy Fallback: If still empty, match key component descriptors (e.g. 'door', 'wheel')
            if (taskQueue.length === 0 && selectedComponent.name) {
              const words = selectedComponent.name.toLowerCase().split(/\s+/).filter(w => w.length > 2);
              const keyWords = ['door', 'wheel', 'hood', 'wing', 'tire', 'glass', 'chassis', 'exhaust', 'seat', 'mirror', 'bumper', 'lights'];
              const matchedKeyWord = words.find(w => keyWords.includes(w));
              
              if (matchedKeyWord) {
                const compNameLower = selectedComponent.name.toLowerCase();
                const isLeftComp = compNameLower.includes('driver') || compNameLower.includes('left') || compNameLower.includes('fl') || compNameLower.includes('_l_');
                const isRightComp = compNameLower.includes('passenger') || compNameLower.includes('right') || compNameLower.includes('fr') || compNameLower.includes('_r_');

                console.log(`⚠️ [ViewerCanvas Mini-Loader] Fuzzy matching hierarchy for key word: "${matchedKeyWord}", Side: ${isLeftComp ? 'Left (Driver)' : isRightComp ? 'Right (Passenger)' : 'Unspecified'}`);
                
                taskQueue = [...manifest.hierarchy].filter(item => {
                  const itemName = (item.name || '').toLowerCase();
                  if (!itemName.includes(matchedKeyWord)) return false;
                  
                  if (isLeftComp) {
                    return itemName.includes('_fl_') || itemName.includes('_l_') || itemName.includes('_left_') || itemName.includes('fl');
                  }
                  if (isRightComp) {
                    return itemName.includes('_fr_') || itemName.includes('_r_') || itemName.includes('_right_') || itemName.includes('fr');
                  }
                  return true;
                });
              }
            }
          }

          const totalTasks = taskQueue.length || 1;

          const workerThread = async () => {
            // Symmetrical parallel optimization: Instantiate a separate GLTFLoader per active worker thread.
            // This prevents serialization bottlenecks inside Three.js parser state-machines, restoring original blazing speeds,
            // while keeping Web Worker context allocations flat to prevent browser crashes on mobile!
            const manager = new THREE.LoadingManager();
            manager.setURLModifier((url) => {
              if (url.startsWith('blob:') || url.startsWith('data:')) {
                return url;
              }
              return `${baseFolder}${url}`;
            });

            const chunkLoader = new GLTFLoader(manager);
            chunkLoader.setDRACOLoader(dracoLoader);

            // Exponential Backoff Fetch Helper to absorb Supabase Storage 429 rate limit exceptions!
            const fetchWithRetry = async (url, retries = 10, delay = 500) => {
              for (let i = 0; i < retries; i++) {
                try {
                  const res = await fetch(url);
                  if (res.ok) return res;
                  if (res.status === 429 && i < retries - 1) {
                    const backoff = delay * Math.pow(2, i) + Math.random() * 200;
                    console.warn(`[429 Rate Limited] Retrying chunk fetch in ${Math.round(backoff)}ms for: ${url}`);
                    await new Promise(r => setTimeout(r, backoff));
                    continue;
                  }
                  throw new Error(`Fetch responded with status: ${res.status}`);
                } catch (err) {
                  if (i === retries - 1) throw err;
                  const backoff = delay * Math.pow(2, i) + Math.random() * 200;
                  await new Promise(r => setTimeout(r, backoff));
                }
              }
            };

            while (taskQueue.length > 0) {
              if (!active) break;
              
              const item = taskQueue.shift();
              if (!item) break;

              // Symmetrical throttling: Yield to the main browser thread to keep UI completely responsive.
              // We use a small, non-zero yield delay of 15ms on desktop and 30ms on mobile to limit request spikes and
              // completely absorb Supabase Storage 429/502 Gateway rate-limiting while keeping load speeds blazing fast!
              if (isMobile) {
                await new Promise(r => setTimeout(r, 30));
              } else {
                await new Promise(r => setTimeout(r, 15));
              }

              try {
                // Symmetrical Cache Flattening: Append a cache-busting timestamp to the URL.
                // This forces the browser NOT to store or read binary chunks from the network RAM cache,
                // while bypassing Safari's strict WebKit-specific fetch TypeError exceptions with cache overrides!
                const chunkUrl = `${baseFolder}${item.chunkName}?v=${loadSessionId}`;
                const chunkRes = await fetchWithRetry(chunkUrl);
                if (!active) break;
                if (!chunkRes.ok) throw new Error(`Chunk fetch failed: ${item.chunkName}`);
                
                let rawBuffer = await chunkRes.arrayBuffer();
                if (!active) break;
                let deobfuscated = deobfuscateBuffer(rawBuffer);

                // Symmetrical Memory Isolation: Convert raw binary ArrayBuffer to a temporary Blob URL.
                const blob = new Blob([deobfuscated], { type: 'model/gltf-binary' });
                const blobUrl = URL.createObjectURL(blob);

                // Actively dereference raw buffers so browser GC can clean them immediately
                deobfuscated = null;
                rawBuffer = null;

                await new Promise((resolveChunk) => {
                  chunkLoader.load(blobUrl, (gltfChunk) => {
                    const topNode = gltfChunk.scene.children[0];
                    if (topNode) {
                      // 1. Symmetrical coordinate reassembly on the chunk's top-level container node
                      topNode.position.fromArray(item.position);
                      topNode.quaternion.fromArray(item.rotation);
                      topNode.scale.fromArray(item.scale);
                      topNode.name = item.name;
                      topNode.uuid = item.meshUuid;

                      // Symmetrical Deterministic ID: Use the manifest hierarchy index to guarantee stable, 
                      // deterministic component-X IDs independent of parallel completion order!
                      const stableIdx = manifest.hierarchy.indexOf(item);
                      const compId = `component-${stableIdx + 1}`;
                      const compName = topNode.name.trim() || `Mesh ${stableIdx + 1}`;

                      // 2. Symmetrical leaf mesh metadata propagation
                      topNode.traverse((node) => {
                        if (node.isMesh) {
                          // Bind search/selection index IDs to the leaf mesh
                          node.userData.componentIds = [compId, ...(item.componentIds || [])];
                          node.userData.originalPosition = node.position.clone();
                          node.userData.originalRotation = node.rotation.clone();

                          // Pre-calculate perfect CAD-grade explode direction for each leaf mesh
                          if (node.geometry) {
                            if (!node.geometry.boundingBox) {
                              node.geometry.computeBoundingBox();
                            }
                            const localBox = node.geometry.boundingBox;
                            const localSize = new THREE.Vector3();
                            localBox.getSize(localSize);
                            const radius = localSize.length() * 0.5;
                            
                            const meshCenter = new THREE.Vector3();
                            localBox.getCenter(meshCenter);
                            meshCenter.applyMatrix4(node.matrixWorld);
                            
                            const direction = new THREE.Vector3().subVectors(meshCenter, new THREE.Vector3(0,0,0));
                            const dist = direction.length();
                            direction.normalize();
                            
                            const pushFactor = (0.2 + dist * 0.5) * (1.0 + radius * 0.8);
                            node.userData.explodeDir = direction.multiplyScalar(pushFactor);
                          } else {
                            node.userData.explodeDir = new THREE.Vector3(0, 0, 0);
                          }

                          // Symmetrical Material Cache & Matte PBR overrides
                          if (node.material) {
                            const mfgEnvIntensity = (lighting?.environment ?? 30) / 100;
                            const mats = Array.isArray(node.material) ? node.material : [node.material];
                            
                            const updatedMats = mats.map(mat => {
                              if (!mat) return mat;
                              
                              // Avoid Deep-Cloning: Materials loaded per-chunk GLB are already fully unique and decoupled.
                              // Deep-cloning 1,113 materials creates massive CPU/memory bottlenecks. Reference mat directly!

                              // Caching original values for correct wireframe/x-ray toggle resets!
                              if (!mat.userData.originalValues) {
                                mat.userData.originalValues = {
                                  wireframe: mat.wireframe,
                                  transparent: mat.transparent,
                                  opacity: mat.opacity,
                                  depthWrite: mat.depthWrite,
                                };
                              }
                              // Symmetrical Environment Map slider linking:
                              if ('envMapIntensity' in mat) mat.envMapIntensity = mfgEnvIntensity * 0.25;
                              
                              return mat;
                            });
                            
                            node.material = Array.isArray(node.material) ? updatedMats : updatedMats[0];
                          }

                          // Capture originalMaterial AFTER the matte PBR overrides are applied!
                          // This ensures unselected components always revert back to their correct matte settings!
                          node.userData.originalMaterial = node.material;
                        }
                      });

                      // 3. Extract component for index list mapping
                      extractedList.push({
                        id: compId,
                        name: compName,
                        type: topNode.type,
                        group: topNode.parent?.name || 'main',
                        meshUuid: topNode.uuid,
                        dbComponentId: item.componentIds?.[0] || null,
                        chunkName: item.chunkName,
                      });

                      // Symmetrical Static Matrix Baking:
                      // Pre-calculate local transform matrices and disable dynamic matrix updates for all static chunk meshes!
                      // This reduces CPU frame-time calculations from O(N) to O(1) and delivers incredibly smooth OrbitControls!
                      topNode.traverse(node => {
                        if (node.isMesh) {
                          node.updateMatrix();
                          node.matrixAutoUpdate = false;
                        }
                      });

                      // 4. Batch appends to prevent WebGL layout thrashing:
                      // Appends meshes in batches of 5 to maintain progressive visual pop-in
                      // while reducing Three.js draw-call and layout compilation overhead by 98%!
                      pendingNodes.push(topNode);
                      if (pendingNodes.length >= 5 || loadedCount >= manifest.hierarchy.length) {
                        while (pendingNodes.length > 0) {
                          group.add(pendingNodes.shift());
                        }
                      }
                    }
                    
                    // Clear temporary chunk scene containers and nullify GLTF internal references
                    if (gltfChunk) {
                      if (gltfChunk.scene) {
                        gltfChunk.scene.clear();
                      }
                      gltfChunk.animations = null;
                      gltfChunk.cameras = null;
                      gltfChunk.scenes = null;
                      gltfChunk.scene = null;
                      gltfChunk.parser = null; // Un-link parser reference cache to reclaim RAM
                    }
                    
                    loadedCount++;
                    setChunkProgress(Math.round((loadedCount / totalTasks) * 100));
                    
                    // Revoke the Object URL instantly and clear Three.js's file RAM Cache to reclaim memory!
                    URL.revokeObjectURL(blobUrl);
                    THREE.Cache.clear();

                    // Symmetrical Garbage Collection (GC) Yield:
                    // Yielding the thread via setTimeout(..., 0) tells the browser's main thread
                    // to pause, run a layout/paint cycle, and process the microtask garbage sweeps.
                    // On mobile, every 8 chunks we extend this yield to 30ms to give WebKit's GC 
                    // a wide, safe window to fully sweep and reclaim discarded memory buffers, completely
                    // preventing mid-load browser OOM crashes even on massive assemblies!
                    if (isMobile && loadedCount % 8 === 0) {
                      setTimeout(resolveChunk, 30);
                    } else {
                      setTimeout(resolveChunk, 0);
                    }
                  }, undefined, (err) => {
                    console.error('Error loading sub-mesh chunk:', item.chunkName, err);
                    URL.revokeObjectURL(blobUrl);
                    
                    // Symmetrical Progress Increment on Failure:
                    loadedCount++;
                    setChunkProgress(Math.round((loadedCount / totalTasks) * 100));
                    
                    setTimeout(resolveChunk, 0);
                  });
                });
              } catch (chunkErr) {
                console.error('❌ Worker task execution failed:', chunkErr);
                
                // Symmetrical Progress Increment on Catch Failure:
                loadedCount++;
                setChunkProgress(Math.round((loadedCount / totalTasks) * 100));
              }
            }
          };

          // Spawn concurrent worker threads with a staggered startup delay
          // This spreads out initial chunk fetch spikes, completely absorbing Supabase CDN 429 rate limits!
          const workers = Array(Math.min(concurrencyLimit, taskQueue.length))
            .fill(null)
            .map(async (_, idx) => {
              if (idx > 0) {
                await new Promise(r => setTimeout(r, idx * 300));
              }
              await workerThread();
            });

          await Promise.all(workers);
          
          if (!active) return; // Discard finalizations if loader was cancelled or unmounted

          // Symmetrical Flush: Append any leftover meshes from the final progressive batch to the scene group!
          while (pendingNodes.length > 0) {
            group.add(pendingNodes.shift());
          }

          // Attach layout/camera metadata to the reassembled group
          group.userData.hasEmbeddedCamera = true;
          group.userData.cameras = []; // Reassembled scene runs at base transformations

          // Symmetrical dynamic grid/floor alignment:
          // Once the model is fully reassembled, calculate its bounding box and 
          // set the FloorY coordinate so the Grid and ContactShadows align perfectly at the bottom!
          group.updateMatrixWorld(true);
          const modelBox = new THREE.Box3().setFromObject(group);
          if (setFloorY) {
            setFloorY(modelBox.min.y);
          }

          if (!isMini) {
            // Cache the extracted components list in extractionCache so clicking the 3D meshes can resolve them
            extractionCache.set(modelUrl, extractedList);
            
            // Dispatch full extracted list to trigger index.jsx state and selectors
            onComponentsExtracted(extractedList);
          }
        } catch (err) {
          if (active) console.error('Failed to dynamically assemble secure model chunks:', err);
        }
      };

      assembleModel();

      // Return a cleanup cancellation hook to abort concurrent loads in StrictMode!
      return () => {
        active = false;
      };
    } else {
      console.warn('🚨 R3F Tracing Audit - MONOLITHIC LOADER EXECUTING for:', modelUrl);
      setChunkProgress(0); // Symmetrically reset progress to 0 to trigger loading spinner!
      setActiveScene(null); // Symmetrically clear old scene!

      // Symmetrical Monolithic Loader: Bypasses React Suspense completely to prevent double loading/unmounting!
      const loader = new GLTFLoader();
      loader.setDRACOLoader(dracoLoader);
      
      let active = true;
      loader.load(modelUrl, (gltf) => {
        if (!active) return;
        
        const hasCam = gltf.cameras && gltf.cameras.length > 0;
        gltf.scene.userData.hasEmbeddedCamera = hasCam;
        gltf.scene.userData.cameras = gltf.cameras || [];
        
        gltf.scene.updateMatrixWorld(true);
        const modelBox = new THREE.Box3().setFromObject(gltf.scene);
        if (setFloorY) {
          setFloorY(modelBox.min.y);
        }
        
        if (!isMini && onComponentsExtracted) {
          const extracted = [];
          gltf.scene.traverse((node) => {
            if (node.isMesh) {
              extracted.push({
                id: node.name,
                name: node.name,
                type: node.type,
                dbComponentId: node.userData.dbComponentId || null
              });
            }
          });
          onComponentsExtracted(extracted);
        }
        
        setChunkProgress(100);
        setActiveScene(gltf.scene);
      }, undefined, (err) => {
        console.error('Failed to load monolithic model natively:', err);
      });
      
      return () => {
        active = false;
      };
    }
  }, [modelUrl, isChunked]);

  // Dynamically sync environment map intensity from advanced lighting controls
  useEffect(() => {
    if (scene) {
      const mfgEnvIntensity = (lighting?.environment ?? 30) / 100;
      if ('environmentIntensity' in scene) {
        scene.environmentIntensity = mfgEnvIntensity;
      } else {
        scene.traverse(node => {
          if (node.isMesh && node.material && 'envMapIntensity' in node.material) {
            node.material.envMapIntensity = mfgEnvIntensity;
          }
        });
      }
    }
  }, [scene, lighting?.environment]);

  const formatValueWithUnit = (valInMm, type) => {
    if (type === 'angle') {
      return `${valInMm.toFixed(1)}°`;
    }
    
    const conversions = { mm: 1, cm: 10, m: 1000, in: 25.4, ft: 304.8 };
    const scale = conversions[unit] || 1;
    const converted = valInMm / scale;
    
    if (type === 'area') {
      const convertedArea = valInMm / (scale * scale);
      return `${convertedArea.toFixed(1)} ${unit}²`;
    }
    if (type === 'radius') {
      return `R = ${converted.toFixed(1)} ${unit}`;
    }
    return `${converted.toFixed(1)} ${unit}`;
  };

  // This effect runs to extract component data and calculate the global center of mass
  useEffect(() => {
    // Symmetrical Standard Loading Guard:
    // Symmetrically return early and wait if the standard monolithic GLB has not resolved yet,
    // preventing premature empty cache population on intermediate loading frames!
    if (!isChunked && !activeScene) {
      return;
    }

    const targetNode = activeScene || groupRef.current;
    if (targetNode) {
      // Symmetrical Jump-Prevention & Dual-Centering Map:
      // 1. Initial Pass: Center on the first chunk mount (children > 0) so it loads in-place near the target.
      // 2. Stable Centering Lock: Reuses the stable initial center calculated on the first pass to completely prevent jumping at 100% progress.
      const canInitializeCenter = !isChunked 
        ? (!!activeScene && !isCentered)
        : (groupRef.current && groupRef.current.children.length > 0 && !isCentered);

      // If it is chunked and we haven't loaded any meshes yet, wait!
      if (isChunked && groupRef.current && groupRef.current.children.length === 0) {
        return;
      }

      // Force update all world matrices to ensure worldToLocal is accurate
      targetNode.updateMatrixWorld(true);

      // Symmetrical dynamic scaling and positioning: 
      const modelCameras = activeScene?.userData?.cameras || [];
      const hasEmbeddedCamera = isChunked || (modelCameras && modelCameras.length > 0);
      const scaleFactor = hasEmbeddedCamera ? 1 : 2;

      // Calculate the global center of the model for centering and the explosion effect
      const modelBox = new THREE.Box3().setFromObject(targetNode);
      const computedCenter = new THREE.Vector3();
      modelBox.getCenter(computedCenter);

      if (canInitializeCenter) {
        // Update state so the primitive object or chunked group is translated symmetrically!
        setModelCenter(computedCenter);
        setIsCentered(true);

        if (isChunked && chunkProgress === 100) {
          isFinalCenteredRef.current = true; // Lock final centering pass!
        }

        // Dynamically align the ground plane shadows and grid to the exact bottom bounds of the model!
        const bottomY = (modelBox.min.y - computedCenter.y) * scaleFactor + modelYShift;
        if (setFloorY) {
          setFloorY(bottomY);
        }

        // Symmetrical: Fit camera and zoom to fit inside mini-viewer so the component fills the window!
        if (isMini) {
          const distance = 4.2; // highly optimized stable closeup CAD zoom distance!
          const camPos = new THREE.Vector3(distance, distance * 0.65, distance);
          camera.position.copy(camPos);
          camera.lookAt(0, 0.45, 0);
          camera.updateProjectionMatrix();
          if (setCameraPosition) {
            setCameraPosition([camPos.x, camPos.y, camPos.z]);
          }
          if (controlsRef?.current) {
            controlsRef.current.target.set(0, 0.45, 0);
            controlsRef.current.update();
            
            // Cache standard coordinates for resets!
            initialPosRef.current.copy(camPos);
            initialTargetRef.current.set(0, 0.45, 0);
            controlsRef.current.customReset = () => {
              camera.position.copy(initialPosRef.current);
              camera.lookAt(0, 0.45, 0);
              controlsRef.current.target.set(0, 0.45, 0);
              controlsRef.current.update();
            };
          }
        } else if (modelCameras && modelCameras.length > 0) {
          const firstEmbeddedCam = modelCameras[0];
          
          // Find the actual camera node inside activeScene graph to extract true world transforms!
          let cameraNode = activeScene.getObjectByName(firstEmbeddedCam.name) || 
                           activeScene.getObjectByProperty('type', 'PerspectiveCamera') || 
                           activeScene.getObjectByProperty('type', 'OrthographicCamera');
          
          if (!cameraNode) {
            // If the camera is not a child node inside activeScene graph, manually attach it so it inherits Deno/Three's automatically applied Z-to-Y rotations!
            activeScene.add(firstEmbeddedCam);
            cameraNode = firstEmbeddedCam;
          }
          
          // Force updating world matrix on scene tree and cameraNode to ensure world positions are computed freshly
          activeScene.updateMatrixWorld(true);
          cameraNode.updateMatrixWorld(true);

          // Extract true world position and world rotation quaternion (fully resolving Blender coordinate hierarchies!)
          const worldPos = new THREE.Vector3();
          const worldQuat = new THREE.Quaternion();
          cameraNode.getWorldPosition(worldPos);
          cameraNode.getWorldQuaternion(worldQuat);
          
          // Match the extracted coordinates with the primitive scale to align with the centered origin!
          // Symmetrical translation: Subtract computedCenter from worldPos to match the centered translation offset!
          const camPos = worldPos.clone().sub(computedCenter).multiplyScalar(scaleFactor);
          
          // Copy camera position directly and force it to look exactly at the center of mass!
          // This is 100% robust and centers the actual center of mass rather than the model pivot origin.
          camera.position.copy(camPos);
          camera.lookAt(0, 0, 0);
          
          if (cameraNode.fov) {
            camera.fov = cameraNode.fov;
          } else if (firstEmbeddedCam.fov) {
            camera.fov = firstEmbeddedCam.fov;
          }
          camera.updateProjectionMatrix();
          
          // Sync position through React state so R3F Canvas preserves it during re-renders!
          if (setCameraPosition) {
            setCameraPosition([camPos.x, camPos.y, camPos.z]);
          }
          
          if (controlsRef?.current) {
            // Since the model is shifted to the origin [0,0,0], lookAtTarget is set symmetrically!
            const lookAtTarget = new THREE.Vector3(0, 0, 0);

            controlsRef.current.target.copy(lookAtTarget);
            controlsRef.current.update();

            // Symmetrical: Cache coordinates to enable 100% mathematically identical reset views!
            initialPosRef.current.copy(camPos);
            initialQuatRef.current.copy(worldQuat);
            initialTargetRef.current.copy(lookAtTarget);

            controlsRef.current.customReset = () => {
              camera.up.set(0, 1, 0); // Restore upright up-vector mathematically!
              camera.position.copy(initialPosRef.current);
              camera.lookAt(0, 0, 0);
              controlsRef.current.target.copy(initialTargetRef.current);
              controlsRef.current.update();
              
              // Push back position update to parent React state to maintain sync!
              if (setCameraPosition) {
                setCameraPosition([initialPosRef.current.x, initialPosRef.current.y, initialPosRef.current.z]);
              }
            };
          }
        }
      }
      
      // Since the model is shifted by -computedCenter, the virtual model center is exactly [0,0,0]!
      const trueModelCenter = new THREE.Vector3(0, 0, 0);

      // Symmetrical Chunk Assembly Guard:
      // Don't write to cache or dispatch parts list to the drawer until the chunked assembly is 100% complete!
      if (isChunked && chunkProgress < 100) {
        return;
      }

      if (hasExtractedRef.current === modelUrl) {
        return; // Symmetrical: Avoid infinite parent re-render cascades!
      }
      hasExtractedRef.current = modelUrl;

      // --- Symmetrical Extraction Cache Guard ---
      // Evaluated AFTER centering and camera alignments have completed,
      // guaranteeing that cached standard models are centered and visible!
      if (!isMini && extractionCache.has(modelUrl)) {
        const cached = extractionCache.get(modelUrl);
        if (!isCentered) {
          onComponentsExtracted(cached.extracted);
          setModelCenter(cached.computedCenter);
          setIsCentered(true);
        }
        return;
      }

      const extracted = [];
      let counter = 1;
      const mfgEnvIntensity = (lighting?.environment ?? 30) / 100;
      targetNode.traverse(node => {
        if (node.isMesh) {
          // Dynamic spotlight hotspots prevention: Cover standard, physical, phong, and specular materials
          if (node.material) {
            // Symmetrical Environment Map slider linking:
            if ('envMapIntensity' in node.material) {
              node.material.envMapIntensity = mfgEnvIntensity * 0.25;
            }
          }

          if (!node.userData.originalPosition) {
            node.userData.originalPosition = node.position.clone();
            node.userData.originalRotation = node.rotation.clone();
            
            // Symmetrical O(1) performance optimization:
            // Instead of calling THREE.Box3().setFromObject(node) (which is O(N^2) recursively traversing all children and matrices), 
            // we read the cached geometry local bounding box instantly and project it using the world matrix!
            if (node.geometry) {
              if (!node.geometry.boundingBox) {
                node.geometry.computeBoundingBox();
              }
              const localBox = node.geometry.boundingBox;
              
              const localSize = new THREE.Vector3();
              localBox.getSize(localSize);
              const radius = localSize.length() * 0.5; // Compute radius instantly!

              const meshCenter = new THREE.Vector3();
              localBox.getCenter(meshCenter);
              meshCenter.applyMatrix4(node.matrixWorld); // Convert local center to true world coords instantly!

              // Compute explosion offset in world-space coordinates pointing away from model center
              const direction = new THREE.Vector3().subVectors(meshCenter, modelCenter);
              const dist = direction.length();
              direction.normalize();

              // Balanced CAD-grade blend: combine size-based push with distance-based push so that larger parts separate properly and center parts don't get stuck!
              const pushFactor = (0.2 + dist * 0.5) * (1.0 + radius * 0.8);
              const worldExplodeDir = direction.multiplyScalar(pushFactor);

              // Safely map world-space destination back into parent local space
              if (node.parent) {
                const worldTarget = meshCenter.clone().add(worldExplodeDir);
                const localTarget = node.parent.worldToLocal(worldTarget);
                node.userData.explodeDir = localTarget.sub(node.userData.originalPosition);
              } else {
                node.userData.explodeDir = worldExplodeDir;
              }
            } else {
              node.userData.explodeDir = new THREE.Vector3(0, 0, 0);
            }
          }

          // --- START of change ---
          const processMaterial = (mat) => {
            if (!mat.userData.originalValues) {
              mat.userData.originalValues = {
                wireframe: mat.wireframe,
                transparent: mat.transparent,
                opacity: mat.opacity,
                depthWrite: mat.depthWrite,
              };
            }
          };

          if (Array.isArray(node.material)) {
            node.material.forEach(processMaterial);
          } else if (node.material) {
            processMaterial(node.material);
          }
          // --- END of change ---

          if (!node.userData.originalMaterial) {
            node.userData.originalMaterial = node.material;
          }
          const name = node.name.trim() || (node.material && node.material.name.trim()) || `Mesh ${counter}`;
          const component = {
            id: `component-${counter++}`,
            name,
            type: node.type,
            group: node.parent?.name || 'main',
            meshUuid: node.uuid,
          };
          
          if (!node.userData.componentIds) node.userData.componentIds = [];
          node.userData.componentIds.push(component.id);
          
          extracted.push(component);
        }
      });
      if (!isMini) {
        extractionCache.set(modelUrl, { extracted, computedCenter });
        onComponentsExtracted(extracted);
      }
    }
  }, [activeScene, modelUrl, onComponentsExtracted, isInfiniteVertical, chunkProgress]);
  
  // Symmetrical Universal Controls target alignment:
  // Runs whenever the model center or controls mount, ensuring the OrbitControls target
  // is ALWAYS locked slightly above the translated model's exact center of mass (+0.35 units offset).
  // This forces the camera to look slightly upward, shifting the rendered model downward in the frame
  // to completely clear upper marquees and lower control overlays perfectly!
  useEffect(() => {
    if (controlsRef.current && modelCenter) {
      const targetY = isMini ? 0.45 : 0;
      controlsRef.current.target.set(0, targetY, 0);
      controlsRef.current.update();
    }
  }, [controlsRef.current, modelCenter, isMini]);
  
  // This effect handles visibility changes for both chunked assemblies and standard monolithic GLB meshes
  useEffect(() => {
    if (groupRef.current) {
        groupRef.current.traverse(node => {
            if (node.isMesh) {
                // Symmetrical: Completely skip visibility overrides for custom subbed GLB aftermarket overlays!
                // They carry their own independent visibility lifecycles!
                if (node.userData.isSubbedGlb === true || node.parent?.userData?.isSubbedGlb === true) {
                  node.visible = true;
                  return;
                }

                // Symmetrical safety: skip visibility updates for parts active inside repair guides to prevent 1-frame conflicts!
                const nodeName = node.name || '';
                if (repairGuideOffsets?.[nodeName]) {
                  return;
                }

                node.visible = !checkNodeIsHidden(node, componentVisibility, componentWraps);
            }
        });
    }
  }, [componentVisibility, componentWraps, repairGuideOffsets]);

  // Symmetrical: Ensure original positions and rotations are always cached ONLY after the model is fully centered and stable!
  useEffect(() => {
    if (isCentered && groupRef.current) {
      groupRef.current.traverse((node) => {
        if (node.isMesh) {
          if (node.userData.originalPosition === undefined) {
            node.userData.originalPosition = node.position.clone();
          }
          if (node.userData.originalRotation === undefined) {
            node.userData.originalRotation = node.rotation.clone();
          }
        }
      });
    }
  }, [isCentered, activeScene]);

  // This effect handles material overrides for selection, view modes, and custom colors
  useEffect(() => {
    if (!groupRef.current) return;
    
    const updatedMaterials = new Set();

    groupRef.current.traverse((node) => {
      if (node.isMesh && node.userData.originalMaterial) {
        const componentIds = node.userData.componentIds || [];
        
        const materialNames = [];
        if (Array.isArray(node.material)) {
          node.material.forEach(m => { if (m?.name) materialNames.push(m.name); });
        } else if (node.material?.name) {
          materialNames.push(node.material.name);
        }

        // Symmetrical Selection Highlight Glow Suppression inside Wrap Designer Material mode:
        // Hide selection glows during wrap customization so technicians can review actual decal mapping alignments cleanly!
        const isMeshSelectedInMulti = isMultiSelectActive && selectedComponentIds.length > 0 && (
          selectedComponentIds.includes(node.name) ||
          componentIds.some(id => selectedComponentIds.includes(id)) ||
          materialNames.some(m => selectedComponentIds.includes(m))
        );

        const nodeClean = cleanMeshName(node.name);
        const selNameClean = cleanMeshName(selectedComponent?.name?.split(' for ')[0] || '');
        const selCustomClean = cleanMeshName(selectedComponent?.customName?.split(' for ')[0] || '');
        const selIdClean = cleanMeshName(selectedComponent?.id || '');
        const selDbIdClean = cleanMeshName(selectedComponent?.dbComponentId || '');
        const selGlobalIdClean = cleanMeshName(selectedComponent?.globalComponentId || '');

        const isSelected = !isMini && (isMeshSelectedInMulti || (
          selectedComponent && viewMode !== 'wrap' && (
            componentIds.some(id => {
              const idStr = id?.toString()?.toLowerCase();
              return idStr === selectedComponent.id?.toString()?.toLowerCase() ||
                     idStr === selectedComponent.dbComponentId?.toString()?.toLowerCase() ||
                     idStr === selectedComponent.globalComponentId?.toString()?.toLowerCase() ||
                     idStr === selectedComponent.sellerComponentId?.toString()?.toLowerCase();
            }) ||
            (nodeClean && (
              nodeClean === selNameClean ||
              nodeClean === selCustomClean ||
              nodeClean === selIdClean ||
              nodeClean === selDbIdClean ||
              nodeClean === selGlobalIdClean ||
              (selNameClean && (nodeClean.includes(selNameClean) || selNameClean.includes(nodeClean))) ||
              (selCustomClean && (nodeClean.includes(selCustomClean) || selCustomClean.includes(nodeClean)))
            )) ||
            (selectedComponent.availableOptions && selectedComponent.availableOptions.some(opt => {
              const optIdClean = cleanMeshName(opt.id || opt.sellerComponentId);
              const optNameClean = cleanMeshName(opt.name || opt.customName);
              return optIdClean === nodeClean || (optNameClean && (optNameClean === nodeClean || nodeClean.includes(optNameClean) || optNameClean.includes(nodeClean)));
            }))
          )
        ));

        let customColor = null;
        const nodeCompIds = node.userData.componentIds || componentIds || [];
        for (const key of [node.name, ...nodeCompIds]) {
          if (key && componentColors && componentColors[key]) {
            customColor = componentColors[key];
            break;
          }
        }
        
        // Symmetrical Wildcard Matching: Only allow wildcard '*' if isMini is true or if there are no other specific component keys in componentColors!
        if (!customColor && componentColors && componentColors['*']) {
          const hasSpecificKeys = Object.keys(componentColors).some(k => k !== '*' && k !== 'all' && k !== 'global');
          if (isMini || !hasSpecificKeys) {
            customColor = componentColors['*'];
          }
        }

        let activeWrap = null;
        for (const key of [node.name, ...materialNames, ...componentIds]) {
          if (key && componentWraps?.[key]) {
            activeWrap = componentWraps[key];
            break;
          }
        }
        if (!activeWrap && componentWraps) {
          const matsClean = materialNames.map(cleanMeshName);
          const idsClean = componentIds.map(cleanMeshName);
          
          for (const key of Object.keys(componentWraps)) {
            const keyClean = cleanMeshName(key);
            if (keyClean && (keyClean === nodeClean || matsClean.includes(keyClean) || idsClean.includes(keyClean))) {
              activeWrap = componentWraps[key];
              break;
            }
          }
        }

        let targetMaterial = node.userData.originalMaterial || node.material;
        if (targetMaterial && !node.userData.originalMaterial) {
          node.userData.originalMaterial = targetMaterial; // Symmetrically cache original unmutated material on-the-fly!
        }

        // Check if this specific mesh node is flagged for Individual Component Z-Ray:
        const isMeshInZRay = zRayComponentIds && zRayComponentIds.length > 0 && (
          zRayComponentIds.some(id => {
            if (!id) return false;
            const idStr = id.toString().toLowerCase();
            const idClean = cleanMeshName(id);
            return (
              idStr === node.name.toLowerCase() ||
              componentIds.some(cId => cId?.toString()?.toLowerCase() === idStr) ||
              (nodeClean && idClean && (nodeClean === idClean || nodeClean.includes(idClean) || idClean.includes(nodeClean)))
            );
          })
        );

        if (isMeshInZRay) {
          // Individual Component Z-Ray: Render ONLY this selected component in high-tech translucent Z-Ray!
          const zRayMat = Array.isArray(targetMaterial) ? targetMaterial.map(m => m.clone()) : targetMaterial.clone();

          const applyZRay = (mat) => {
            mat.transparent = true;
            mat.opacity = 0.32;
            mat.depthWrite = false;
            if ('color' in mat && mat.color) {
              mat.color.set('#00e5ff');
            }
            if ('emissive' in mat && mat.emissive) {
              mat.emissive.set('#00ffff').multiplyScalar(0.4);
            }
            if ('roughness' in mat) mat.roughness = 0.1;
            if ('metalness' in mat) mat.metalness = 0.1;
            mat.wireframe = (viewMode === 'wireframe');
            mat.needsUpdate = true;
          };

          if (Array.isArray(zRayMat)) {
            zRayMat.forEach(applyZRay);
          } else {
            applyZRay(zRayMat);
          }

          if (node.material !== zRayMat) {
            node.material = zRayMat;
          }
          return; // Skip other material overrides for individual Z-Ray meshes!
        }

        if (isSelected) {
          // Dynamic Glowing Selection Highlight:
          // Clones the mesh's original material, maintaining 100% of its native details, textures, 
          // normal maps, lighting shadows, and metallic reflections, but overlays a glowing green tint!
          const highlightMat = Array.isArray(targetMaterial) ? targetMaterial.map(m => m.clone()) : targetMaterial.clone();
          
          const applyHighlight = (mat) => {
            mat.transparent = false;
            mat.opacity = 1.0;
            mat.depthWrite = true;
            if ('color' in mat && mat.color) {
              // Symmetrically: If a custom color is specified for this selected component, use it as the base highlight color,
              // otherwise KEEP the original material color (do not override to solid green)!
              // Custom colors should ONLY be painted in Material viewmode! In shaded mode, keep original color!
              const colVal = (viewMode === 'material' && customColor) ? (typeof customColor === 'object' ? customColor.color : customColor) : null;
              if (colVal) {
                mat.color.set(colVal);
              } else if (isMeshSelectedInMulti) {
                mat.color.set('#6366f1');
              } else if (mat.userData?.originalValues?.color) {
                mat.color.copy(mat.userData.originalValues.color);
              }
            }
            if ('emissive' in mat && mat.emissive) {
              mat.emissive.set(isMeshSelectedInMulti ? '#4f46e5' : '#22c55e').multiplyScalar(0.18); // Sleek overlay glow!
            }
            if ('roughness' in mat) mat.roughness = 0.5; // Maintain pristine reflection details
            if ('metalness' in mat) mat.metalness = 0.3;
            mat.wireframe = (viewMode === 'wireframe');
            mat.needsUpdate = true;
          };

          if (Array.isArray(highlightMat)) {
            highlightMat.forEach(applyHighlight);
          } else {
            applyHighlight(highlightMat);
          }

          if (node.material !== highlightMat) {
            node.material = highlightMat;
          }
          return; // Selected items get the custom high-detail highlight material.
        }

        const cleanName = (str) => {
          if (!str) return '';
          return str.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        };

        // Symmetrical B2B Diagnostic Highlight UUID & Case-Insensitive Resolver:
        // Automatically maps database record UUID keys back to their corresponding physical GLTF mesh names
        // utilizing the extracted model components list, fully curing all highlight mismatches!
        const getDiagnosticStatus = (nodeName, compIds, diagRecords, url) => {
          if (!diagRecords) return null;
          
          const cached = extractionCache.get(url);
          const extracted = cached ? (Array.isArray(cached) ? cached : (cached.extracted || [])) : [];

          for (const key of Object.keys(diagRecords)) {
            // Find if the diagnostic record key is a component UUID in our extracted model list
            const comp = extracted.find(c => c.id?.toString().toLowerCase() === key.toLowerCase() || c.dbComponentId?.toString().toLowerCase() === key.toLowerCase());
            const resolvedName = comp ? (comp.name || comp.id) : key;
            
            const isMatch = (cleanName(resolvedName) === cleanName(nodeName)) ||
                            compIds.some(id => cleanName(id) === cleanName(resolvedName));
                            
            if (isMatch) {
              return diagRecords[key];
            }
          }
          return null;
        };

        const diagnosticStatus = getDiagnosticStatus(node.name, componentIds, diagnosticRecords, modelUrl);

        if (diagnosticStatus) {
          const diagMat = Array.isArray(targetMaterial) ? targetMaterial.map(m => m.clone()) : targetMaterial.clone();
          const applyDiag = (mat) => {
            if ('color' in mat && mat.color) {
              mat.color.set(diagnosticStatus === 'replaced' || diagnosticStatus === 'repaired' ? '#10b981' : '#ef4444');
            }
            if ('emissive' in mat && mat.emissive) {
              mat.emissive.set(diagnosticStatus === 'replaced' || diagnosticStatus === 'repaired' ? '#047857' : '#b91c1c').multiplyScalar(0.4);
            }
            if ('roughness' in mat) mat.roughness = 0.5;
            if ('metalness' in mat) mat.metalness = 0.3;
            mat.wireframe = (viewMode === 'wireframe');
            mat.needsUpdate = true;
          };
          if (Array.isArray(diagMat)) {
            diagMat.forEach(applyDiag);
          } else {
            applyDiag(diagMat);
          }
          if (node.material !== diagMat) {
            node.material = diagMat;
          }
          return;
        }

        // Apply pre-compiled globally shared MeshBasicMaterial for unselected parts during Z-Ray Gray Vision.
        // If manual component Z-Ray is active, unselected parts must remain in their normal shaded material!
        const isManualZRayActive = zRayComponentIds && zRayComponentIds.length > 0;
        if (!isManualZRayActive && (viewMode === 'x-ray-gray' || viewMode === 'xray-gray')) {
          sharedXrayMaterial.wireframe = (viewMode === 'wireframe');
          if (node.material !== sharedXrayMaterial) {
            node.material = sharedXrayMaterial;
          }
          return; // Done with X-ray setup
        }

        // Symmetrical Material Isolation:
        // Highly optimized: Only clone/isolate the material if it is actually being painted or wrapped
        // to prevent massive GPU shader compilation spikes and freeze locks!
        let materialNeedsCloning = !!(customColor || activeWrap);

        if (materialNeedsCloning) {
          targetMaterial = Array.isArray(targetMaterial) ? targetMaterial.map(m => m.clone()) : targetMaterial.clone();
        }

        const applyEffects = (mat) => {
          // Optimization: Don't process the same material multiple times in one pass
          if (updatedMaterials.has(mat)) return;
          updatedMaterials.add(mat);

          let changed = false;

          // Symmetrical Self-Healing originalValues caching:
          // If originalValues is missing on first render pass, cache it on the fly!
          if (!mat.userData.originalValues) {
            mat.userData.originalValues = {
              wireframe: mat.wireframe || false,
              transparent: mat.transparent || false,
              opacity: mat.opacity !== undefined ? mat.opacity : 1.0,
              depthWrite: mat.depthWrite !== undefined ? mat.depthWrite : true,
            };
          }

          // Always reset to original values before applying effects
          if(mat.userData.originalValues) {
            if (mat.wireframe !== mat.userData.originalValues.wireframe) { mat.wireframe = mat.userData.originalValues.wireframe; changed = true; }
            if (mat.transparent !== mat.userData.originalValues.transparent) { mat.transparent = mat.userData.originalValues.transparent; changed = true; }
            if (mat.opacity !== mat.userData.originalValues.opacity) { mat.opacity = mat.userData.originalValues.opacity; changed = true; }
            if (mat.depthWrite !== (mat.userData.originalValues.depthWrite ?? true)) { mat.depthWrite = mat.userData.originalValues.depthWrite ?? true; changed = true; }
          }

          if (viewMode === 'wireframe') { mat.wireframe = true; changed = true; }

          // Symmetrical Satin CAD Default for untextured/raw CAD models:
          // Prevents untextured meshes from appearing ultra-glossy, while fully preserving
          // native glossy/metallic chrome reflections on fully textured PBR or vertex-colored models!
          if (viewMode === 'shaded' || viewMode === 'material') {
            if (!mat.map && !mat.normalMap && !mat.roughnessMap && !mat.metalnessMap && !mat.vertexColors) {
              if ('roughness' in mat && mat.roughness === 1.0) { mat.roughness = 0.65; changed = true; }
              if ('metalness' in mat && mat.metalness === 0.0) { mat.metalness = 0.1; changed = true; }
            }
          }

          // Symmetrical Z-Ray Color: Mutate original material opacity on-the-fly, fully retaining native base colors!
          if (viewMode === 'x-ray' || viewMode === 'xray') {
            mat.transparent = true;
            mat.opacity = 0.25; // Sleek translucent color rendering
            mat.depthWrite = false;
            changed = true;
          }
          
          // Symmetrical B2C/B2B 3D Wrap custom configurations:
          if (activeWrap) {
            if (activeWrap.color) {
              mat.color.set(activeWrap.color);
              changed = true;
            }
            if (activeWrap.roughness !== undefined) {
              mat.roughness = activeWrap.roughness;
              changed = true;
            }
            if (activeWrap.metalness !== undefined) {
              mat.metalness = activeWrap.metalness;
              changed = true;
            }
            if (activeWrap.decalUrl) {
              const tex = getCachedTexture(activeWrap.decalUrl, () => {
                mat.needsUpdate = true;
              });
              if (tex) {
                // Symmetrical Advanced UV Matrix Transformations & Tiling Toggles!
                const wrapping = activeWrap.decalTiling ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
                tex.wrapS = wrapping;
                tex.wrapT = wrapping;
                
                const scaleX = activeWrap.decalScaleX !== undefined ? parseFloat(activeWrap.decalScaleX) : 1.0;
                const scaleY = activeWrap.decalScaleY !== undefined ? parseFloat(activeWrap.decalScaleY) : 1.0;
                tex.repeat.set(1.0 / scaleX, 1.0 / scaleY);
                
                const offsetX = activeWrap.decalOffsetX !== undefined ? parseFloat(activeWrap.decalOffsetX) : 0.0;
                const offsetY = activeWrap.decalOffsetY !== undefined ? parseFloat(activeWrap.decalOffsetY) : 0.0;
                tex.offset.set(offsetX, offsetY);
                
                tex.center.set(0.5, 0.5);
                tex.rotation = activeWrap.decalRotation !== undefined ? parseFloat(activeWrap.decalRotation) : 0.0;
              }
              if (mat.map !== tex) {
                mat.map = tex;
                changed = true;
              }
            }
          } else if (customColor && viewMode === 'material') {
            const hasColorObj = typeof customColor === 'object';
            const col = hasColorObj ? customColor.color : customColor;
            if (col) {
              mat.color.set(col);
            }
            if (hasColorObj) {
              if (customColor.metalness !== undefined && 'metalness' in mat) {
                mat.metalness = parseFloat(customColor.metalness) || 0;
              }
              if (customColor.roughness !== undefined && 'roughness' in mat) {
                mat.roughness = parseFloat(customColor.roughness) || 0;
              }
            }
            changed = true;
          }
          
          if (changed) mat.needsUpdate = true;
        };

        if (Array.isArray(targetMaterial)) {
          targetMaterial.forEach(applyEffects);
        } else {
          applyEffects(targetMaterial);
        }
        
        if (node.material !== targetMaterial) {
          node.material = targetMaterial;
        }
      }
    });

    return () => {
      // Cleanup: Revert all materials to their original state
      if (groupRef.current) {
        groupRef.current.traverse((node) => {
          if (node.isMesh && node.userData.originalMaterial) {
            if (node.material !== node.userData.originalMaterial) {
              node.material = node.userData.originalMaterial;
            }
          }
        });
      }
    };
  }, [viewMode, selectedComponent, zRayComponentIds, componentColors, activeScene, diagnosticRecords, componentWraps]);

  const { explosionAmount } = useSpring({
    explosionAmount: isExploded ? 1 : 0,
    config: { mass: 1, tension: 170, friction: 26 }
  });

  const offsetActiveRef = useRef(false);

  useFrame((state) => {
    if (groupRef.current) {
      // Symmetrical Enterprise Repair Guide Sliding, Rotating and Dematerializing Blink animation loop:
      if (transitionStartTimeRef.current === -1) {
        transitionStartTimeRef.current = state.clock.getElapsedTime();
      }

      const elapsed = state.clock.getElapsedTime() - transitionStartTimeRef.current;
      const slideDuration = 1.0; // 1.0s to slide
      const blinkDuration = 0.6; // 0.6s to blink rapidly
      const totalDuration = slideDuration + blinkDuration;

      // Symmetrical smooth Glide camera viewport tracking angle transition!
      if (cameraLerpStartTimeRef.current === -1) {
        cameraLerpStartTimeRef.current = state.clock.getElapsedTime();
      }
      const camElapsed = state.clock.getElapsedTime() - cameraLerpStartTimeRef.current;
      if (cameraLerpTargetRef.current && cameraLerpPosRef.current && camElapsed < 1.8 && controlsRef?.current) {
        controlsRef.current.target.lerp(cameraLerpTargetRef.current, 0.08);
        camera.position.lerp(cameraLerpPosRef.current, 0.08);
      }

      groupRef.current.traverse((node) => {
        if (node.isMesh) {
          const nodeName = node.name || '';
          const offset = repairGuideOffsets?.[nodeName];

          // Symmetrical dragging guard: Do not animate/overwrite position of selected mesh in builder mode!
          const isDraggingThisMesh = repairBuilderActive && selectedComponent && 
            (nodeName.toLowerCase() === selectedComponent.name?.toLowerCase());

          // Symmetrical safe-caching guard: Only apply translations/resets if original coordinates have finished caching!
          if (offset && node.userData.originalPosition && node.userData.originalRotation) {
            const targetPos = new THREE.Vector3(
              node.userData.originalPosition.x + (offset.x || 0),
              node.userData.originalPosition.y + (offset.y || 0),
              node.userData.originalPosition.z + (offset.z || 0)
            );

            const targetRot = new THREE.Euler(
              node.userData.originalRotation.x + (offset.rx || 0) * (Math.PI / 180),
              node.userData.originalRotation.y + (offset.ry || 0) * (Math.PI / 180),
              node.userData.originalRotation.z + (offset.rz || 0) * (Math.PI / 180)
            );

            let finalPos = targetPos.clone();
            if (offset.px || offset.py || offset.pz) {
              const localHinge = new THREE.Vector3(offset.px || 0, offset.py || 0, offset.pz || 0);
              const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(targetRot);
              const rotatedHinge = localHinge.clone().applyMatrix4(rotationMatrix);
              finalPos.add(localHinge).sub(rotatedHinge);
            }

            if (isDraggingThisMesh) {
              return;
            }

            const isStandardVisible = !checkNodeIsHidden(node, componentVisibility, componentWraps);

            // Phase A: Slide & Slerp (0s to 1.0s)
            if (elapsed < slideDuration) {
              node.position.lerp(finalPos, 0.12);
              
              const qTarget = new THREE.Quaternion().setFromEuler(targetRot);
              node.quaternion.slerp(qTarget, 0.12);

              node.visible = isStandardVisible;
            } 
            // Phase B: Dematerializing Rapid Blink (1.0s to 1.6s)
            else if (offset.isRemoved && elapsed < totalDuration) {
              node.position.copy(finalPos);
              node.rotation.copy(targetRot);

              // 15Hz rapid visibility flash
              const blink = Math.floor(elapsed * 20) % 2 === 0;
              node.visible = blink && isStandardVisible;

              // Flash material color to high-contrast red warning
              if (node.isMesh && node.material) {
                if (node.userData.originalMaterialColor === undefined) {
                  if (Array.isArray(node.material)) {
                    node.userData.originalMaterialColor = node.material.map(m => m.color?.clone() || new THREE.Color('#ffffff'));
                  } else if (node.material.color) {
                    node.userData.originalMaterialColor = node.material.color.clone();
                  }
                }
                if (Array.isArray(node.material)) {
                  node.material.forEach(m => m.color?.set(blink ? '#ef4444' : '#ffffff'));
                } else if (node.material.color) {
                  node.material.color.set(blink ? '#ef4444' : '#ffffff');
                }
              }
            } 
            // Phase C: Settled / Hidden (>= 1.6s)
            else {
              node.position.copy(finalPos);
              node.rotation.copy(targetRot);

              if (offset.isRemoved) {
                node.visible = false;
              } else {
                node.visible = isStandardVisible;
              }

              // Restore original material color
              if (node.isMesh && node.material && node.userData.originalMaterialColor !== undefined) {
                if (Array.isArray(node.material)) {
                  node.material.forEach((m, i) => m.color?.copy(node.userData.originalMaterialColor[i]));
                } else if (node.material.color) {
                  node.material.color.copy(node.userData.originalMaterialColor);
                }
              }
            }
            node.updateMatrix(); // Force manual update on-demand for static meshes!
          } else if (node.userData.originalPosition && node.userData.originalRotation) {
            // If no active offset, smoothly return to origin position and rotation
            if (!isDraggingThisMesh) {
              node.position.lerp(node.userData.originalPosition, 0.12);
              
              const qOrigin = new THREE.Quaternion().setFromEuler(node.userData.originalRotation);
              node.quaternion.slerp(qOrigin, 0.12);

              // Standard visibility check
              node.visible = !checkNodeIsHidden(node, componentVisibility, componentWraps);
              node.updateMatrix(); // Force manual update on-demand for static meshes!
            }
          }
        }
      });

      if (isPlaying) {
        groupRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.3) * 0.1;
      }

      const currentExplosion = explosionAmount.get();
      if (currentExplosion > 0.001) {
        offsetActiveRef.current = true;
        groupRef.current.traverse((node) => {
          if (node.isMesh && node.userData.originalPosition && node.userData.explodeDir) {
            // Apply independent CAD-grade scaling on X, Y, and Z axes
            node.position.set(
              node.userData.originalPosition.x + node.userData.explodeDir.x * currentExplosion * 3 * explodeScaleX,
              node.userData.originalPosition.y + node.userData.explodeDir.y * currentExplosion * 3 * explodeScaleY,
              node.userData.originalPosition.z + node.userData.explodeDir.z * currentExplosion * 3 * explodeScaleZ
            );
            node.updateMatrix(); // Manual update on-demand!
          }
        });
      } else if (offsetActiveRef.current) {
        offsetActiveRef.current = false;
        groupRef.current.traverse((node) => {
          if (node.isMesh && node.userData.originalPosition) {
            node.position.copy(node.userData.originalPosition);
            node.updateMatrix(); // Manual update on-demand!
          }
        });
      }

      // Symmetrical Selected Component Pulse Animation:
      // Pulsate emissive intensity dynamically on the selected mesh, and clear deselects on every frame loop!
      const pulseIntensity = 0.4 + Math.sin(state.clock.elapsedTime * 6.0) * 0.3; // High frequency smooth pulse!
      let foundMesh = false;
      
      groupRef.current.traverse((node) => {
        if (node.isMesh && node.material) {
          const componentIds = node.userData.componentIds || [];
          const isSelectedMesh = !isMini && selectedComponent && (
            componentIds.some(id => selectedComponent.id?.toString().toLowerCase() === id?.toLowerCase() || selectedComponent.dbComponentId?.toString().toLowerCase() === id?.toLowerCase()) ||
            node.name.toLowerCase() === selectedComponent.name?.toLowerCase() || 
            node.name.toLowerCase() === selectedComponent.customName?.toLowerCase() ||
            node.name.toLowerCase() === selectedComponent.dbComponentId?.toString().toLowerCase()
          );
          
          const applyPulse = (mat) => {
            if ('emissive' in mat && mat.emissive) {
              if (isSelectedMesh && viewMode !== 'wrap') {
                mat.emissive.set('#22c55e'); // Symmetrical glowing green highlight pulse color!
                mat.emissiveIntensity = pulseIntensity;
              } else {
                // Symmetrical deselect reset: Clear glowing emissive highlight cleanly on deselect!
                // This instantly restores your original custom configured wrap and paint colors!
                mat.emissive.set('#000000');
                mat.emissiveIntensity = 0;
              }
            }
          };

          if (Array.isArray(node.material)) {
            node.material.forEach(applyPulse);
          } else {
            applyPulse(node.material);
          }

          if (isSelectedMesh && viewMode !== 'wrap' && viewMode !== 'material') {
            foundMesh = true;
            // Symmetrical World Vector calculations for hovering drag tags!
            const worldVec = new THREE.Vector3();
            node.getWorldPosition(worldVec);
            worldVec.y += 0.35; // Hover cleanly above the component!
            if (!highlightedWorldPos || worldVec.distanceTo(highlightedWorldPos) > 0.05) {
              setHighlightedWorldPos(worldVec);
            }
          }
        }
      });

      if (!foundMesh && highlightedWorldPos) {
        setHighlightedWorldPos(null);
      }
      
      // Symmetrical Cinematic Sliding Focus target transition:
      // Smoothly glides the camera's focus pivot point (target) to our active focus vector3!
      // This eliminates abrupt snapping jumps on selection, creating beautiful fluid transitions!
      // We wrap the lerp in a sliding lock check. Once the target settles within 0.01 units of the component,
      // we stop updating the target and set isSlidingRef to false, instantly freeing up right-drag panning at will!
      if (isSlidingRef.current && controlsRef.current) {
        const dist = controlsRef.current.target.distanceTo(lerpTargetRef.current);
        if (dist > 0.01) {
          controlsRef.current.target.lerp(lerpTargetRef.current, 0.08);
          controlsRef.current.update();
        } else {
          controlsRef.current.target.copy(lerpTargetRef.current); // Hard copy target on settle!
          isSlidingRef.current = false; // Symmetrically release the sliding lock!
        }
      }
    }
  });
  
  // Symmetrical Auto-Focus: Center camera target on selected swapped parts so they never render off-frame!
  // We strictly ONLY focus when the selection ID changes, completely preventing any camera movements during drags!
  useEffect(() => {
    if (selectedComponent) {
      const wrap = componentWraps[selectedComponent.id];
      if (wrap && wrap.position) {
        // Transition camera focus center to custom swapped component's active world coordinates smoothly!
        lerpTargetRef.current.set(wrap.position[0], wrap.position[1], wrap.position[2]);
        isSlidingRef.current = true; // Symmetrical: Lock the slider target transition loop on!
      }
    } else {
      // Symmetrical reset: Focus target centers smoothly back to the default base vehicle model's center [0, 0.45, 0] if isMini!
      lerpTargetRef.current.set(0, isMini ? 0.45 : 0, 0);
      isSlidingRef.current = true; // Symmetrical: Lock the return transition loop on!
    }
  }, [selectedComponent, isMini]); // Strictly omit componentWraps to prevent dragging coordinate feedback loops!

  const handleModelClick = (event) => {
    event.stopPropagation();
    
    // Safety check: if the click hit a fine-tuning marker sphere first, do NOT process this as a model mesh click!
    const hitMarker = event.intersections?.some(hit => hit.object.name === 'tuning-marker');
    if (hitMarker) return;

    if (activeMeasurementTool === 'distance' || activeMeasurementTool === 'angle' || activeMeasurementTool === 'radius' || activeMeasurementTool === 'area' || activeMeasurementTool === 'perimeter') {
      const clickPoint = event.point;
      onAddMeasurementPoint(clickPoint);
      return;
    }
    const component = findComponent(event.intersections, modelUrl, componentWraps);
    if (onComponentClick) {
      onComponentClick(component || null, event.ctrlKey || event.metaKey);
    }

    // Symmetrically enable HTML5 draggability ONLY on clicking direct selected component mesh!
    if (component && selectedComponent && allowDragToInvoice && setCanvasDraggable) {
      const isClickOnSelected = (
        component.name?.toLowerCase() === selectedComponent.name?.toLowerCase() ||
        component.id?.toString().toLowerCase() === selectedComponent.id?.toString().toLowerCase()
      );
      if (isClickOnSelected) {
        console.log('🎯 3D mesh click registered inside selected component! Enabling HTML5 canvas drag...');
        setCanvasDraggable(true);
      } else {
        setCanvasDraggable(false);
      }
    } else if (setCanvasDraggable) {
      setCanvasDraggable(false);
    }
  };

  const handleModelDoubleClick = (event) => {
    event.stopPropagation();
    if (activeMeasurementTool) return; // Disable menu during measurements

    const component = findComponent(event.intersections, modelUrl, componentWraps);
    if (component && onComponentDoubleClick) {
      onComponentDoubleClick(component);
    }
  };

  // Symmetrical Mini-Viewer Mesh Isolation & Zoom-To-Fit Focus:
  // If isMini is true, recursively hide all meshes except those that belong to the selected component!
  // Immediately after, calculate the physical center of mass of the isolated visible meshes,
  // translate the mesh to local origin [0,0,0], and zoom fit the camera perfectly inside the window!
  useEffect(() => {
    if (!selectedComponent) {
      if (isMini && activeScene && (chunkProgress === 100 || !isChunked) && controls) {
        const box = new THREE.Box3().setFromObject(activeScene);
        const center = new THREE.Vector3();
        box.getCenter(center);
        const size = new THREE.Vector3();
        box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z);
        if (maxDim > 0) {
          activeScene.position.set(-center.x, -center.y, -center.z);
          controls.target.set(0, 0.45, 0);
          const distance = 2.5;
          const camPos = new THREE.Vector3(distance, distance * 0.65, distance);
          camera.position.copy(camPos);
          camera.lookAt(0, 0.45, 0);
          camera.updateProjectionMatrix();
          if (setCameraPosition) {
            setCameraPosition([camPos.x, camPos.y, camPos.z]);
          }
          controls.update();
        }
      }
      hasIsolatedRef.current = null;
      return;
    }
    if (isMini && activeScene && (chunkProgress === 100 || !isChunked) && controls) {
      if (!selectedComponent) {
        return;
      }
      const selectId = selectedComponent.globalComponentId || 
                       selectedComponent.componentId || 
                       selectedComponent.component_id || 
                       selectedComponent.component?.id ||
                       selectedComponent.dbComponentId || 
                       selectedComponent.id;

      if (hasIsolatedRef.current === selectId) {
        return;
      }
      hasIsolatedRef.current = selectId;
      // Symmetrically resolve the true Mesh Name ID (e.g. "door_lf") from componentWraps map dynamically!
      let resolvedMeshName = '';
      if (componentWraps && selectId) {
        for (const [meshKey, wrap] of Object.entries(componentWraps)) {
          if (wrap.componentId === selectId || wrap.id === selectId || meshKey === selectId) {
            resolvedMeshName = meshKey;
            break;
          }
        }
      }
      
      const selectIdClean = cleanMeshName(resolvedMeshName || selectedComponent.globalComponentId || selectedComponent.id || '');
      const selectNameClean = cleanMeshName(selectedComponent.name || '');
      
      // 1. Isolate and physically REMOVE unselected meshes from the cloned scene graph
      let matchCount = 0;
      const toRemove = [];
      activeScene.traverse(node => {
        if (node.isMesh) {
          const nodeNameClean = cleanMeshName(node.name);
          const itemCompIds = node.userData.componentIds || node.componentIds || [];
          const cleanCompIds = itemCompIds.map(cleanMeshName);
          
          const isMatch = (selectIdClean && (nodeNameClean === selectIdClean || nodeNameClean.includes(selectIdClean) || selectIdClean.includes(nodeNameClean) || cleanCompIds.includes(selectIdClean))) ||
                          (selectNameClean && (nodeNameClean === selectNameClean || nodeNameClean.includes(selectNameClean) || selectNameClean.includes(nodeNameClean) || cleanCompIds.includes(selectNameClean)));
                          
          let isFuzzyMatch = false;
          if (!isMatch && selectedComponent.name) {
            const words = selectedComponent.name.toLowerCase().split(/\s+/).filter(w => w.length > 2);
            const keyWords = ['door', 'wheel', 'hood', 'wing', 'tire', 'glass', 'chassis', 'exhaust', 'seat', 'mirror', 'bumper', 'lights'];
            const matchedKeyWord = words.find(w => keyWords.includes(w));
            if (matchedKeyWord && nodeNameClean.includes(matchedKeyWord)) {
              const compNameLower = selectedComponent.name.toLowerCase();
              const isLeftComp = compNameLower.includes('driver') || compNameLower.includes('left') || compNameLower.includes('fl') || compNameLower.includes('_l_');
              const isRightComp = compNameLower.includes('passenger') || compNameLower.includes('right') || compNameLower.includes('fr') || compNameLower.includes('_r_');

              if (isLeftComp) {
                isFuzzyMatch = nodeNameClean.includes('fl') || nodeNameClean.includes('_l_') || nodeNameClean.includes('left');
              } else if (isRightComp) {
                isFuzzyMatch = nodeNameClean.includes('fr') || nodeNameClean.includes('_r_') || nodeNameClean.includes('right');
              } else {
                isFuzzyMatch = true;
              }
            }
          }

          if (isMatch || isFuzzyMatch) {
            matchCount++;
            node.visible = true;
          } else {
            toRemove.push(node);
          }
        }
      });

      // Symmetrical Fallback: If meshes matched, physically delete all other unselected meshes to load JUST the selected component!
      if (matchCount > 0) {
        toRemove.forEach(mesh => {
          if (mesh.parent) {
            mesh.parent.remove(mesh);
          }
        });
      } else {
        // Fallback: If absolutely no meshes matched, restore visibility to ALL meshes so we don't render a blank scene!
        activeScene.traverse(node => {
          if (node.isMesh) node.visible = true;
        });
      }

      // 2. Compute bounding box and center of mass of the isolated visible meshes
      const box = new THREE.Box3().setFromObject(activeScene);
      const center = new THREE.Vector3();
      box.getCenter(center);
      
      const size = new THREE.Vector3();
      box.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z);
      
      if (maxDim > 0 && maxDim !== Infinity && !isNaN(center.x) && !isNaN(center.y) && !isNaN(center.z)) {
        setIsolatedCount(prev => prev + 1); // Trigger re-scaling and centering on isolated meshes!
      }
    }
  }, [isMini, activeScene, selectedComponent, chunkProgress, isChunked, controls]);

  // Symmetrical Camera and Controls Alignment for Isolated meshes:
  // Runs on the next render cycle when isolatedCount updates and the component's snug dimensions are fully resolved!
  useEffect(() => {
    if (isMini && activeScene && isolatedCount > 0 && controls) {
      const localBox = new THREE.Box3();
      const prevPos = activeScene.position.clone();
      activeScene.position.set(0, 0, 0);
      localBox.setFromObject(activeScene);
      activeScene.position.copy(prevPos);
      
      const center = new THREE.Vector3();
      localBox.getCenter(center);
      const size = new THREE.Vector3();
      localBox.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z);

      if (maxDim > 0 && maxDim !== Infinity && !isNaN(center.x)) {
        controls.target.set(0, 0.45, 0); // Symmetrically target world origin offset to move model down/back!
        
        // Symmetrically calculate snug closeup camera distance (2.5) so the component fills the window!
        const distance = 2.5; // Optimized stable closeup CAD zoom distance for scaled mesh!
        const camPos = new THREE.Vector3(distance, distance * 0.65, distance);
        camera.position.copy(camPos);
        camera.lookAt(0, 0.45, 0);
        camera.updateProjectionMatrix();
        
        if (setCameraPosition) {
          setCameraPosition([camPos.x, camPos.y, camPos.z]);
        }
        controls.update();
      }
    }
  }, [isMini, activeScene, isolatedCount, controls]);

  const scale = hasEmbeddedCamera ? 1 : 2;

  // Symmetrically compute a dynamic scale factor for the mini-viewer so that the component fills most of the window!
  const miniScale = useMemo(() => {
    if (!isMini || !activeScene) return 1;
    // Symmetrical Native Box Solver: setFromObject automatically ignores hidden parts AND accounts for all local scales/transforms!
    const localBox = new THREE.Box3().setFromObject(activeScene);
    const size = new THREE.Vector3();
    localBox.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    if (maxDim > 0) {
      // We want the component's maximum dimension to be exactly 3.0 units inside R3F!
      // This is 100% CAD-grade precise and forces ANY component (no matter how small!) to render larger than the whole model!
      return 3.0 / maxDim;
    }
    return 1;
  }, [isMini, activeScene, chunkProgress, isolatedCount]);

  const activeScale = isMini ? miniScale : scale;

  // Symmetrical Declarative Position Centering for Mini-Viewers:
  // Dynamically computes the snug negative offset center of the isolated component meshes,
  // pivot shifting them cleanly to world origin [0,0,0] via props to avoid R3F value overrides!
  const activePosition = useMemo(() => {
    if (isMini && activeScene) {
      const localBox = new THREE.Box3();
      const prevPos = activeScene.position.clone();
      activeScene.position.set(0, 0, 0);
      localBox.setFromObject(activeScene);
      activeScene.position.copy(prevPos);
      
      const center = new THREE.Vector3();
      localBox.getCenter(center);
      if (!isNaN(center.x) && !isNaN(center.y) && !isNaN(center.z)) {
        return [-center.x, -center.y, -center.z];
      }
    }
    return [0, 0, 0];
  }, [isMini, activeScene, chunkProgress, isolatedCount]);

  // Symmetrical On-the-fly Center of Mass Translation:
  // Forcefully shift the model's position by the negative center of mass vector scaled by the primitive scale factor,
  // translating the geometric center of mass exactly to world origin [0,0,0] inside the viewer boundaries!
  const position = isMini ? [0, 0, 0] : [
    -(modelCenter?.x || 0) * activeScale, 
    -(modelCenter?.y || 0) * activeScale + modelYShift * activeScale, 
    -(modelCenter?.z || 0) * activeScale
  ];

  // Symmetrical Clean Assembly: Standard models are hidden until centered (prevents 1-frame jump).
  // Chunked models are displayed progressively as each part is loaded, giving the user live progress visibility!
  const groupVisible = isCentered;

  return (
    <group ref={groupRef} visible={groupVisible} position={position}>
      {activeScene ? (
        <primitive
          object={activeScene}
          scale={activeScale}
          position={isMini ? activePosition : [0, 0, 0]}
          onClick={handleModelClick}
          onDoubleClick={handleModelDoubleClick}
          onContextMenu={(e) => {
          e.stopPropagation();
          if (activeMeasurementTool) return;
          const component = findComponent(e.intersections, modelUrl, componentWraps);
          if (component && onContextMenuRequest) {
            const x = e.nativeEvent?.clientX || e.clientX || 0;
            const y = e.nativeEvent?.clientY || e.clientY || 0;
            onContextMenuRequest(component, x, y);
          }
          }}
          />
          ) : chunkProgress < 100 ? (
          <LoadingSpinner progress={chunkProgress} />
          ) : null}

      {builderTargetMesh && repairBuilderActive && (
        <TransformControls
          object={builderTargetMesh}
          mode={gizmoMode}
          size={0.8}
          onObjectChange={() => {
            if (builderTargetMesh && onRepairPartMoved && selectedComponent) {
              const dx = builderTargetMesh.position.x - (builderTargetMesh.userData.originalPosition?.x || 0);
              const dy = builderTargetMesh.position.y - (builderTargetMesh.userData.originalPosition?.y || 0);
              const dz = builderTargetMesh.position.z - (builderTargetMesh.userData.originalPosition?.z || 0);
              
              const drx = (builderTargetMesh.rotation.x - (builderTargetMesh.userData.originalRotation?.x || 0)) * (180 / Math.PI);
              const dry = (builderTargetMesh.rotation.y - (builderTargetMesh.userData.originalRotation?.y || 0)) * (180 / Math.PI);
              const drz = (builderTargetMesh.rotation.z - (builderTargetMesh.userData.originalRotation?.z || 0)) * (180 / Math.PI);
              
              onRepairPartMoved(selectedComponent.name, dx, dy, dz, drx, dry, drz);
            }
          }}
          onDraggingChange={(e) => {
            const dragging = e?.value !== undefined ? e.value : (e?.target?.dragging ?? false);
            if (controls) {
              controls.enabled = !dragging;
            }
          }}
        />
      )}

      {/* Symmetrical extraction path string lines (dashed guide connectors) */}
      {showStringLines && Object.entries(repairGuideOffsets).map(([nodeName, offset]) => {
        let nodeRef = null;
        groupRef.current?.traverse((node) => {
          if (node.name === nodeName) {
            nodeRef = node;
          }
        });

        if (!nodeRef || !nodeRef.userData.originalPosition) return null;

        // Create line points from original position to current animated position
        const p1 = nodeRef.userData.originalPosition;
        const p2 = nodeRef.position;

        return (
          <Line
            key={nodeName}
            points={[p1, p2]}
            color="#38bdf8" // Cyan-blue high-fidelity guide line!
            lineWidth={2.5}
            dashed
            dashSize={0.08}
            gapSize={0.04}
            depthTest={false} // Disable depth testing so it is visible through other parts!
          />
        );
      })}

      {/* Symmetrical Custom Swapped Component GLB Overlays */}
      {Object.entries(componentWraps)
        .filter(([compId, wrap]) => wrap.subbedGlbUrl && wrap.subbedGlbUrl.trim() !== '')
        .map(([compId, wrap]) => {
          const isSelected = selectedComponent && (selectedComponent.id === compId || selectedComponent.name === compId);
          return (
            <Suspense key={compId} fallback={null}>
              <SubbedGlbModel 
                compId={compId}
                wrap={wrap}
                onUpdate={onUpdateComponentWrap}
                isSelected={isSelected}
                gizmoMode={gizmoMode}
                onClick={onComponentClick} // Symmetrical: Map standard click selection!
                onDoubleClick={onComponentDoubleClick} // Symmetrical: Map double click!
                scale={scale} // Symmetrical: Pass the global model centering scale factor down!
                gizmoSize={gizmoSize} // Symmetrical: Pass active gizmo size scale factor down!
                onGizmoSizeChange={onGizmoSizeChange} // Symmetrical: Pass active gizmo size updater down!
              />
            </Suspense>
          );
        })}

      {/* Active Measurement Points (Red markers) */}
      {measurementPoints.map((p, idx) => (
        <mesh key={idx} position={p}>
          <sphereGeometry args={[0.015, 16, 16]} />
          <meshBasicMaterial color="#ef4444" depthTest={false} transparent opacity={0.9} />
        </mesh>
      ))}

      {/* Active Area Picking Lines */}
      {activeMeasurementTool === 'area' && measurementPoints.length >= 2 && (
        <>
          <line>
            <bufferGeometry attach="geometry" onUpdate={self => self.setFromPoints(measurementPoints)} />
            <lineBasicMaterial attach="material" color="#ef4444" linewidth={2} depthTest={false} />
          </line>
          {measurementPoints.length >= 3 && (
            <line>
              <bufferGeometry attach="geometry" onUpdate={self => self.setFromPoints([measurementPoints[measurementPoints.length - 1], measurementPoints[0]])} />
              <lineBasicMaterial attach="material" color="#ef4444" linewidth={2} dashed dashSize={0.1} gapSize={0.05} depthTest={false} />
            </line>
          )}
          {measurementPoints.length >= 3 && (
            <Html position={measurementPoints[0]} center>
              <button
                onClick={(e) => { e.stopPropagation(); onCompleteArea(); }}
                className="px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white font-bold rounded shadow-lg animate-pulse text-[10px] whitespace-nowrap cursor-pointer flex items-center gap-1 border border-green-400"
              >
                ✓ Complete Area
              </button>
            </Html>
          )}
        </>
      )}

      {/* Active Perimeter Picking Lines */}
      {activeMeasurementTool === 'perimeter' && measurementPoints.length >= 2 && (
        <>
          <line>
            <bufferGeometry attach="geometry" onUpdate={self => self.setFromPoints(measurementPoints)} />
            <lineBasicMaterial attach="material" color="#ef4444" linewidth={2} depthTest={false} />
          </line>
          {measurementPoints.length >= 3 && (
            <line>
              <bufferGeometry attach="geometry" onUpdate={self => self.setFromPoints([measurementPoints[measurementPoints.length - 1], measurementPoints[0]])} />
              <lineBasicMaterial attach="material" color="#ef4444" linewidth={2} dashed dashSize={0.1} gapSize={0.05} depthTest={false} />
            </line>
          )}
          {measurementPoints.length >= 2 && (
            <Html position={measurementPoints[0]} center>
              <button
                onClick={(e) => { e.stopPropagation(); onCompletePerimeter(); }}
                className="px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white font-bold rounded shadow-lg animate-pulse text-[10px] whitespace-nowrap cursor-pointer flex items-center gap-1 border border-green-400"
              >
                ✓ Complete Perimeter
              </button>
            </Html>
          )}
        </>
      )}

      {/* Render Saved Area Measurements */}
      {measurements?.filter(m => m.type === 'area')?.map((m) => {
        if (!m.points || m.points.length < 3) return null;
        const pathPoints = m.points.map(p => new THREE.Vector3(...p));
        const closedPoints = [...pathPoints, pathPoints[0]];
        
        const center = new THREE.Vector3();
        pathPoints.forEach(p => center.add(p));
        center.divideScalar(pathPoints.length);
        
        return (
          <group key={`${m.id}-${m.points?.map(p => p.join(',')).join('|')}`}>
            {/* Emerald markers */}
            {pathPoints.map((p, idx) => (
              <mesh key={idx} position={p}>
                <sphereGeometry args={[0.01, 16, 16]} />
                <meshBasicMaterial color="#10b981" depthTest={false} transparent opacity={0.9} />
              </mesh>
            ))}
            
            {/* Boundary lines */}
            <line>
              <bufferGeometry attach="geometry" onUpdate={self => self.setFromPoints(closedPoints)} />
              <lineBasicMaterial attach="material" color="#10b981" linewidth={2.5} depthTest={false} />
            </line>
            
            {/* Area Label */}
            <Html position={center} center>
              <div className="bg-slate-900/95 text-emerald-400 font-bold px-2 py-0.5 rounded border border-emerald-500/30 text-[9px] shadow-lg whitespace-nowrap select-none pointer-events-none mb-4">
                {formatValueWithUnit(m.value, 'area')}
              </div>
            </Html>
          </group>
        );
      })}

      {/* Render Saved Perimeter Measurements */}
      {measurements?.filter(m => m.type === 'perimeter')?.map((m) => {
        if (!m.points || m.points.length < 2) return null;
        const pathPoints = m.points.map(p => new THREE.Vector3(...p));
        const closedPoints = pathPoints.length >= 3 ? [...pathPoints, pathPoints[0]] : pathPoints;
        
        const center = new THREE.Vector3();
        pathPoints.forEach(p => center.add(p));
        center.divideScalar(pathPoints.length);
        
        return (
          <group key={`${m.id}-${m.points?.map(p => p.join(',')).join('|')}`}>
            {/* Markers */}
            {pathPoints.map((p, idx) => (
              <mesh key={idx} position={p}>
                <sphereGeometry args={[0.01, 16, 16]} />
                <meshBasicMaterial color="#ec4899" depthTest={false} transparent opacity={0.9} />
              </mesh>
            ))}
            
            {/* Boundary lines */}
            <line>
              <bufferGeometry attach="geometry" onUpdate={self => self.setFromPoints(closedPoints)} />
              <lineBasicMaterial attach="material" color="#ec4899" linewidth={2.5} depthTest={false} />
            </line>
            
            {/* Perimeter Label */}
            <Html position={center} center>
              <div className="bg-slate-900/95 text-pink-400 font-bold px-2 py-0.5 rounded border border-pink-500/30 text-[9px] shadow-lg whitespace-nowrap select-none pointer-events-none mb-4">
                {formatValueWithUnit(m.value, 'perimeter')}
              </div>
            </Html>
          </group>
        );
      })}

      {/* Render Saved Distance Measurements */}
      {measurements?.filter(m => m.type === 'distance')?.map((m) => {
        if (!m.startPoint || !m.endPoint) return null;
        const start = new THREE.Vector3(...m.startPoint);
        const end = new THREE.Vector3(...m.endPoint);
        const points = [start, end];
        const midPoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
        
        return (
          <group key={`${m.id}-${m.startPoint?.join(',')}-${m.endPoint?.join(',')}`}>
            {/* Start and End Blue Markers */}
            <mesh position={start}>
              <sphereGeometry args={[0.012, 16, 16]} />
              <meshBasicMaterial color="#0ea5e9" depthTest={false} transparent opacity={0.9} />
            </mesh>
            <mesh position={end}>
              <sphereGeometry args={[0.012, 16, 16]} />
              <meshBasicMaterial color="#0ea5e9" depthTest={false} transparent opacity={0.9} />
            </mesh>
            
            {/* Connection Line */}
            <line>
              <bufferGeometry attach="geometry" onUpdate={self => self.setFromPoints(points)} />
              <lineBasicMaterial attach="material" color="#0ea5e9" linewidth={3} depthTest={false} />
            </line>
            
            {/* Label Centered between Points */}
            <Html position={midPoint} center>
              <div className="bg-slate-900/95 text-sky-400 font-bold px-2 py-0.5 rounded border border-sky-500/30 text-[9px] shadow-lg whitespace-nowrap select-none pointer-events-none">
                {formatValueWithUnit(m.value, 'distance')}
              </div>
            </Html>
          </group>
        );
      })}

      {/* Render Saved Angle Measurements */}
      {measurements?.filter(m => m.type === 'angle')?.map((m) => {
        if (!m.startPoint || !m.vertexPoint || !m.endPoint) return null;
        const start = new THREE.Vector3(...m.startPoint);
        const vertex = new THREE.Vector3(...m.vertexPoint);
        const end = new THREE.Vector3(...m.endPoint);
        const points1 = [start, vertex];
        const points2 = [vertex, end];
        
        return (
          <group key={`${m.id}-${m.startPoint?.join(',')}-${m.vertexPoint?.join(',')}-${m.endPoint?.join(',')}`}>
            {/* Three Yellow Markers */}
            <mesh position={start}>
              <sphereGeometry args={[0.012, 16, 16]} />
              <meshBasicMaterial color="#eab308" depthTest={false} transparent opacity={0.9} />
            </mesh>
            <mesh position={vertex}>
              <sphereGeometry args={[0.014, 16, 16]} />
              <meshBasicMaterial color="#eab308" depthTest={false} transparent opacity={0.9} />
            </mesh>
            <mesh position={end}>
              <sphereGeometry args={[0.012, 16, 16]} />
              <meshBasicMaterial color="#eab308" depthTest={false} transparent opacity={0.9} />
            </mesh>
            
            {/* Arm 1 Line */}
            <line>
              <bufferGeometry attach="geometry" onUpdate={self => self.setFromPoints(points1)} />
              <lineBasicMaterial attach="material" color="#eab308" linewidth={3} depthTest={false} />
            </line>
            
            {/* Arm 2 Line */}
            <line>
              <bufferGeometry attach="geometry" onUpdate={self => self.setFromPoints(points2)} />
              <lineBasicMaterial attach="material" color="#eab308" linewidth={3} depthTest={false} />
            </line>
            
            {/* Angle Label centered at the Vertex */}
            <Html position={vertex} center>
              <div className="bg-slate-900/95 text-yellow-400 font-bold px-2 py-0.5 rounded border border-yellow-500/30 text-[9px] shadow-lg whitespace-nowrap select-none pointer-events-none mb-4">
                {formatValueWithUnit(m.value, 'angle')}
              </div>
            </Html>
          </group>
        );
      })}

      {/* Render Saved Radius Measurements */}
      {measurements?.filter(m => m.type === 'radius')?.map((m) => {
        if (!m.startPoint || !m.vertexPoint || !m.endPoint || !m.centerPoint) return null;
        const p1 = new THREE.Vector3(...m.startPoint);
        const p2 = new THREE.Vector3(...m.vertexPoint);
        const p3 = new THREE.Vector3(...m.endPoint);
        const center = new THREE.Vector3(...m.centerPoint);
        
        // Calculate the exact normal of the 3D plane passing through p1, p2, p3
        const p1SubP2 = new THREE.Vector3().subVectors(p1, p2);
        const p3SubP2 = new THREE.Vector3().subVectors(p3, p2);
        const normal = new THREE.Vector3().crossVectors(p1SubP2, p3SubP2).normalize();
        
        // Calculate the quaternion rotation from standard Z-axis (XY plane) to plane normal vector
        const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);
        
        const radiusPoints = [center, p1];
        const radiusInMeters = m.value / 1000;
        
        return (
          <group key={m.id}>
            {/* Orange markers */}
            <mesh position={p1}>
              <sphereGeometry args={[0.012, 16, 16]} />
              <meshBasicMaterial color="#f97316" depthTest={false} transparent opacity={0.9} />
            </mesh>
            <mesh position={p2}>
              <sphereGeometry args={[0.012, 16, 16]} />
              <meshBasicMaterial color="#f97316" depthTest={false} transparent opacity={0.9} />
            </mesh>
            <mesh position={p3}>
              <sphereGeometry args={[0.012, 16, 16]} />
              <meshBasicMaterial color="#f97316" depthTest={false} transparent opacity={0.9} />
            </mesh>
            
            {/* Smooth 3D Circumcircle Outline passing exactly through the three points */}
            <mesh position={center} quaternion={quaternion}>
              <ringGeometry args={[radiusInMeters - 0.0015, radiusInMeters + 0.0015, 64]} />
              <meshBasicMaterial color="#f97316" side={THREE.DoubleSide} depthTest={false} />
            </mesh>
            
            {/* Center pointer to circumferance radius line */}
            <line>
              <bufferGeometry attach="geometry" onUpdate={self => self.setFromPoints(radiusPoints)} />
              <lineBasicMaterial attach="material" color="#f97316" linewidth={2} dashed depthTest={false} />
            </line>
            
            {/* Center marker */}
            <mesh position={center}>
              <sphereGeometry args={[0.008, 16, 16]} />
              <meshBasicMaterial color="#f97316" depthTest={false} />
            </mesh>
            
            {/* Radius Label */}
            <Html position={center} center>
              <div className="bg-slate-900/95 text-orange-400 font-bold px-2 py-0.5 rounded border border-orange-500/30 text-[9px] shadow-lg whitespace-nowrap select-none pointer-events-none mb-4">
                {formatValueWithUnit(m.value, 'radius')}
              </div>
            </Html>
          </group>
        );
      })}

      {/* If a measurement is selected in the list, render its markers with interactive point fine-tuning selection */}
      {measurements?.filter(m => m.id === selectedMeasurementId)?.map((m) => {
        return (
          <group key="selected-tuning">
            {m.startPoint && (
              <mesh 
                name="tuning-marker"
                position={new THREE.Vector3(...m.startPoint)}
                onClick={(e) => { e.stopPropagation(); onSelectPointKey('startPoint'); }}
              >
                <sphereGeometry args={[0.02, 16, 16]} />
                <meshBasicMaterial 
                  color={selectedPointKey === 'startPoint' ? '#22c55e' : '#22c55e'} 
                  wireframe
                  depthTest={false} 
                />
                <Html center>
                  <div 
                    onClick={(e) => { e.stopPropagation(); onSelectPointKey('startPoint'); }}
                    className={`text-[8px] font-bold px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap cursor-pointer transition-all select-none ${
                      selectedPointKey === 'startPoint' ? 'bg-green-600 text-white animate-bounce' : 'bg-slate-900/90 text-green-400 hover:bg-green-500 hover:text-white'
                    }`}
                  >
                    {selectedPointKey === 'startPoint' ? 'Move START here...' : 'Move START'}
                  </div>
                </Html>
              </mesh>
            )}
            
            {m.vertexPoint && (
              <mesh 
                name="tuning-marker"
                position={new THREE.Vector3(...m.vertexPoint)}
                onClick={(e) => { e.stopPropagation(); onSelectPointKey('vertexPoint'); }}
              >
                <sphereGeometry args={[0.02, 16, 16]} />
                <meshBasicMaterial 
                  color={selectedPointKey === 'vertexPoint' ? '#22c55e' : '#22c55e'} 
                  wireframe
                  depthTest={false} 
                />
                <Html center>
                  <div 
                    onClick={(e) => { e.stopPropagation(); onSelectPointKey('vertexPoint'); }}
                    className={`text-[8px] font-bold px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap cursor-pointer transition-all select-none ${
                      selectedPointKey === 'vertexPoint' ? 'bg-green-600 text-white animate-bounce' : 'bg-slate-900/90 text-green-400 hover:bg-green-500 hover:text-white'
                    }`}
                  >
                    {selectedPointKey === 'vertexPoint' ? 'Move VERTEX here...' : 'Move VERTEX'}
                  </div>
                </Html>
              </mesh>
            )}

            {m.endPoint && (
              <mesh 
                name="tuning-marker"
                position={new THREE.Vector3(...m.endPoint)}
                onClick={(e) => { e.stopPropagation(); onSelectPointKey('endPoint'); }}
              >
                <sphereGeometry args={[0.02, 16, 16]} />
                <meshBasicMaterial 
                  color={selectedPointKey === 'endPoint' ? '#22c55e' : '#22c55e'} 
                  wireframe
                  depthTest={false} 
                />
                <Html center>
                  <div 
                    onClick={(e) => { e.stopPropagation(); onSelectPointKey('endPoint'); }}
                    className={`text-[8px] font-bold px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap cursor-pointer transition-all select-none ${
                      selectedPointKey === 'endPoint' ? 'bg-green-600 text-white animate-bounce' : 'bg-slate-900/90 text-green-400 hover:bg-green-500 hover:text-white'
                    }`}
                  >
                    {selectedPointKey === 'endPoint' ? 'Move END here...' : 'Move END'}
                  </div>
                </Html>
              </mesh>
            )}

            {m.points && (m.type === 'area' || m.type === 'perimeter') && m.points.map((pArray, idx) => {
              const pos = new THREE.Vector3(...pArray);
              const pointKey = `point_${idx}`;
              
              return (
                <mesh 
                  name="tuning-marker"
                  key={idx}
                  position={pos}
                  onClick={(e) => { e.stopPropagation(); onSelectPointKey(pointKey); }}
                >
                  <sphereGeometry args={[0.02, 16, 16]} />
                  <meshBasicMaterial 
                    color={selectedPointKey === pointKey ? '#22c55e' : '#22c55e'} 
                    wireframe
                    depthTest={false} 
                  />
                  <Html center>
                    <div 
                      onClick={(e) => { e.stopPropagation(); onSelectPointKey(pointKey); }}
                      className={`text-[8px] font-bold px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap cursor-pointer transition-all select-none ${
                        selectedPointKey === pointKey ? 'bg-green-600 text-white animate-bounce' : 'bg-slate-900/90 text-green-400 hover:bg-green-500 hover:text-white'
                      }`}
                    >
                      {selectedPointKey === pointKey ? `Move Vertex ${idx + 1} here...` : `Move Vertex ${idx + 1}`}
                    </div>
                  </Html>
                </mesh>
              );
            })}
          </group>
        );
      })}
    </group>
  );
};

const LoadingSpinner = ({ progress }) => {
  const { progress: dreiProgress } = useProgress();
  const p = progress !== undefined ? progress : (isNaN(dreiProgress) ? 0 : Math.round(dreiProgress));

  return (
    <Html center>
      <div className="flex flex-col items-center justify-center bg-white/95 backdrop-blur-md border border-slate-100 p-5 rounded-xl text-slate-800 select-none whitespace-nowrap min-w-[220px] shadow-xl text-center">
        <Icon name="Cpu" size={26} className="animate-spin text-indigo-600 mb-2.5 mx-auto" />
        <div className="text-sm font-black tracking-tight text-slate-900">Retrieving 3D Parts</div>
        <div className="text-[11px] text-slate-500 font-bold mt-1.5 bg-slate-50 px-2.5 py-0.5 rounded-full border border-slate-100">{p}% completed</div>
      </div>
    </Html>
  );
};

// Symmetrical File-Level Component Mesh Resolver supporting both parent and child components perfectly!
const findComponent = (intersections, modelUrl, componentWraps = {}) => {
  if (!intersections || intersections.length === 0) return null;
  const cached = extractionCache.get(modelUrl);

  // Support both old array cache and new object cache formats defensively!
  const extracted = cached ? (Array.isArray(cached) ? cached : (cached.extracted || [])) : [];

  // 1. Symmetrical Swapped Aftermarket Component Raycast Interceptor:
  // If they click on a mesh that is a child of an active swapped GLB primitive, map the select!
  for (const hit of intersections) {
    let curr = hit.object;
    while (curr) {
      if (curr.name && componentWraps && componentWraps[curr.name]) {
        const compObj = extracted.find(c => c.id === curr.name || c.name === curr.name);
        return compObj || { id: curr.name, name: curr.name };
      }
      curr = curr.parent;
    }
  }

  // 2. Try to find a hit that has componentIds (chunked models)
  let firstHit = intersections.find(hit => hit.object.isMesh && hit.object.visible && hit.object.userData.componentIds);
  if (firstHit) {
    const componentId = firstHit.object.userData.componentIds[0];
    const match = extracted.find(c => c.dbComponentId === componentId || c.id === componentId);
    if (match) return match;
    return { id: componentId, name: firstHit.object.name || componentId, dbComponentId: componentId };
  }

  // 3. Fallback for standard models: find any visible mesh hit and match by node name or material name!
  firstHit = intersections.find(hit => hit.object.isMesh && hit.object.visible);
  if (firstHit) {
    const nodeName = firstHit.object.name;
    const matched = extracted.find(c => 
      c.name === nodeName || 
      c.customName === nodeName || 
      c.dbComponentId === nodeName ||
      (c.availableOptions && c.availableOptions.some(opt => opt.id === nodeName))
    );
    return matched || { id: nodeName, name: nodeName };
  }
  return null;
};

const kelvinToColor = (temp) => {
  // Quick approximation of Kelvin color temperature for Three.js directional lights
  if (temp < 4000) return '#ffaa44'; // Very warm orange-gold
  if (temp < 5000) return '#ffdca8'; // Warm yellow
  if (temp < 6500) return '#ffffff'; // Pure white
  if (temp < 8000) return '#d4ebff'; // Cool blueish-white
  return '#a8d7ff'; // Ice blue
};

const ViewerCanvas = forwardRef(({
  modelUrl,
  isChunked,
  activeMeasurementTool,
  measurements,
  onAddMeasurement,
  selectedMeasurementId,
  onUpdateMeasurement,
  unit = 'mm',
  chunkProgress,
  setChunkProgress = NOOP,
  onComponentDoubleClick,
  onContextMenuRequest = NOOP,
  isInfiniteVertical = false,
  componentWraps = {},
  bgClass = '',
  allowDragToInvoice = false,
  selectedComponent,
  onComponentClick,
  modelYShift = 0,
  isMultiSelectActive = false,
  selectedComponentIds = [],
  onUpdateComponentWrap = NOOP, // Symmetrical: Destructure onUpdateComponentWrap explicitly!
  gizmoSize = 0.8, // Symmetrical: Receive custom visual visual gizmo scale size!
  onGizmoSizeChange = NOOP, // Symmetrical: Receive active gizmo size scale modifier callback!
  autoRotate = false,
  autoRotateSpeed = 2.0,
  isMini = false,
  repairGuideOffsets = {},
  repairBuilderActive = false,
  onRepairPartMoved = NOOP,
  activeStepIndex = 0,
  activeStepCamera = null,
  showStringLines = false,
  activeStepActions = [],
  ...props
}, ref) => {
  if (!modelUrl || !modelUrl.startsWith('http')) {
    return null; // Bypasses rendering entirely until URL is fully resolved to public HTTP URL!
  }

  const controlsRef = useRef();
  
  const [orbitEnabled, setOrbitEnabled] = React.useState(true); // Symmetrical: Control OrbitControls enabled state reactively!
  const [cameraPosition, setCameraPosition] = React.useState([5, 5, 5]);
  const [floorY, setFloorY] = React.useState(-2.15);
  const [measurementPoints, setMeasurementPoints] = React.useState([]);
  const [selectedPointKey, setSelectedPointKey] = React.useState(null); // 'startPoint', 'vertexPoint', 'endPoint'
  const [activeEnvPreset, setActiveEnvPreset] = React.useState('studio');
  const [canvasDraggable, setCanvasDraggable] = React.useState(false);
  const [isInteracting, setIsInteracting] = React.useState(false);

  // Symmetrical Env Preset Sync:
  // Track and lock the last selected valid Drei preset to prevent unmounts/flicker during advanced slider adjustments.
  useEffect(() => {
    const VALID_DREI_PRESETS = ['apartment', 'city', 'dawn', 'forest', 'lobby', 'night', 'park', 'studio', 'sunset', 'warehouse'];
    if (props.lighting?.preset && VALID_DREI_PRESETS.includes(props.lighting.preset)) {
      setActiveEnvPreset(props.lighting.preset);
    }
  }, [props.lighting?.preset]);

  // Clear start points when measurement tool is deactivated or list length changes
  useEffect(() => {
    setMeasurementPoints([]);
    setSelectedPointKey(null);
  }, [activeMeasurementTool, measurements?.length]);

  // Clear point edit selections if selected measurement changes
  useEffect(() => {
    setSelectedPointKey(null);
  }, [selectedMeasurementId]);

  const handleAddMeasurementPoint = useCallback((point) => {
    // 1. If we are in "Fine-Tuning Point Move" mode, snap the selected point to the click position!
    if (selectedMeasurementId && selectedPointKey) {
      const m = measurements.find(item => item.id === selectedMeasurementId);
      if (m) {
        const updatedFields = {};
        const newCoords = point.toArray();
        
        if (selectedPointKey === 'startPoint') {
          updatedFields.startPoint = newCoords;
        } else if (selectedPointKey === 'vertexPoint') {
          updatedFields.vertexPoint = newCoords;
        } else if (selectedPointKey === 'endPoint') {
          updatedFields.endPoint = newCoords;
        } else if (selectedPointKey.startsWith('point_')) {
          const idx = parseInt(selectedPointKey.split('_')[1]);
          const newPoints = [...m.points];
          newPoints[idx] = newCoords;
          updatedFields.points = newPoints;
        }

        // Recalculate metrics based on new coordinates in real-time
        if (m.type === 'distance') {
          const start = new THREE.Vector3(...(updatedFields.startPoint || m.startPoint));
          const end = new THREE.Vector3(...(updatedFields.endPoint || m.endPoint));
          updatedFields.value = start.distanceTo(end) * 1000;
        } else if (m.type === 'angle') {
          const p1 = new THREE.Vector3(...(updatedFields.startPoint || m.startPoint));
          const p2 = new THREE.Vector3(...(updatedFields.vertexPoint || m.vertexPoint));
          const p3 = new THREE.Vector3(...(updatedFields.endPoint || m.endPoint));
          const v1 = new THREE.Vector3().subVectors(p1, p2);
          const v2 = new THREE.Vector3().subVectors(p3, p2);
          updatedFields.value = v1.angleTo(v2) * (180 / Math.PI);
        } else if (m.type === 'radius') {
          const p1 = new THREE.Vector3(...(updatedFields.startPoint || m.startPoint));
          const p2 = new THREE.Vector3(...(updatedFields.vertexPoint || m.vertexPoint));
          const p3 = new THREE.Vector3(...(updatedFields.endPoint || m.endPoint));
          
          const a = p1.distanceTo(p2);
          const b = p2.distanceTo(p3);
          const c = p3.distanceTo(p1);
          const crossProduct = new THREE.Vector3().crossVectors(
            new THREE.Vector3().subVectors(p1, p2),
            new THREE.Vector3().subVectors(p3, p2)
          );
          const area = 0.5 * crossProduct.length();
          if (area > 0) {
            updatedFields.value = ((a * b * c) / (4 * area)) * 1000;
            // Update center estimation
            updatedFields.centerPoint = new THREE.Vector3().addVectors(p1, p2).add(p3).divideScalar(3).toArray();
          }
        } else if (m.type === 'area' || m.type === 'perimeter') {
          const nPoints = (updatedFields.points || m.points).map(p => new THREE.Vector3(...p));
          
          if (m.type === 'area') {
            let totalArea = 0;
            const p1 = nPoints[0];
            for (let i = 1; i < nPoints.length - 1; i++) {
              const p2 = nPoints[i];
              const p3 = nPoints[i + 1];
              const v1 = new THREE.Vector3().subVectors(p2, p1);
              const v2 = new THREE.Vector3().subVectors(p3, p1);
              const cross = new THREE.Vector3().crossVectors(v1, v2);
              totalArea += 0.5 * cross.length();
            }
            updatedFields.value = totalArea * 1000000; // to square mm
          } else {
            let totalLength = 0;
            for (let i = 0; i < nPoints.length - 1; i++) {
              totalLength += nPoints[i].distanceTo(nPoints[i + 1]);
            }
            if (nPoints.length >= 3) {
              totalLength += nPoints[nPoints.length - 1].distanceTo(nPoints[0]);
            }
            updatedFields.value = totalLength * 1000; // to mm
          }
        }

        onUpdateMeasurement?.(selectedMeasurementId, updatedFields);
        setSelectedPointKey(null);
      }
      return;
    }

    // 2. Standard Click-to-Measure loops
    if (activeMeasurementTool === 'distance') {
      if (measurementPoints.length === 0) {
        setMeasurementPoints([point]);
      } else {
        const start = measurementPoints[0];
        const distance = start.distanceTo(point) * 1000; // in mm
        onAddMeasurement({
          id: Date.now(),
          type: 'distance',
          value: distance,
          startPoint: start.toArray(),
          endPoint: point.toArray(),
          timestamp: Date.now()
        });
        setMeasurementPoints([]);
      }
    } else if (activeMeasurementTool === 'angle') {
      if (measurementPoints.length < 2) {
        setMeasurementPoints(prev => [...prev, point]);
      } else {
        const p1 = measurementPoints[0];
        const p2 = measurementPoints[1]; // Vertex
        const p3 = point;
        
        const v1 = new THREE.Vector3().subVectors(p1, p2);
        const v2 = new THREE.Vector3().subVectors(p3, p2);
        const angleRad = v1.angleTo(v2);
        const angleDeg = angleRad * (180 / Math.PI);
        
        onAddMeasurement({
          id: Date.now(),
          type: 'angle',
          value: angleDeg,
          startPoint: p1.toArray(),
          vertexPoint: p2.toArray(),
          endPoint: p3.toArray(),
          timestamp: Date.now()
        });
        setMeasurementPoints([]);
      }
    } else if (activeMeasurementTool === 'radius') {
      if (measurementPoints.length < 2) {
        setMeasurementPoints(prev => [...prev, point]);
      } else {
        const p1 = measurementPoints[0];
        const p2 = measurementPoints[1];
        const p3 = point;
        
        const a = p1.distanceTo(p2);
        const b = p2.distanceTo(p3);
        const c = p3.distanceTo(p1);
        const crossProduct = new THREE.Vector3().crossVectors(
          new THREE.Vector3().subVectors(p1, p2),
          new THREE.Vector3().subVectors(p3, p2)
        );
        const area = 0.5 * crossProduct.length();
        if (area > 0) {
          const radius = ((a * b * c) / (4 * area)) * 1000; // in mm
          const center = new THREE.Vector3().addVectors(p1, p2).add(p3).divideScalar(3);
          
          onAddMeasurement({
            id: Date.now(),
            type: 'radius',
            value: radius,
            startPoint: p1.toArray(),
            vertexPoint: p2.toArray(),
            endPoint: p3.toArray(),
            centerPoint: center.toArray(),
            timestamp: Date.now()
          });
        }
        setMeasurementPoints([]);
      }
    } else if (activeMeasurementTool === 'area' || activeMeasurementTool === 'perimeter') {
      setMeasurementPoints(prev => [...prev, point]);
    }
  }, [measurementPoints, activeMeasurementTool, onAddMeasurement, selectedMeasurementId, selectedPointKey, measurements, onUpdateMeasurement]);

  const handleCompleteArea = useCallback(() => {
    if (measurementPoints.length < 3) return;
    
    // Recalculate area
    let totalArea = 0;
    const p1 = measurementPoints[0];
    for (let i = 1; i < measurementPoints.length - 1; i++) {
      const p2 = measurementPoints[i];
      const p3 = measurementPoints[i + 1];
      
      const v1 = new THREE.Vector3().subVectors(p2, p1);
      const v2 = new THREE.Vector3().subVectors(p3, p1);
      const cross = new THREE.Vector3().crossVectors(v1, v2);
      totalArea += 0.5 * cross.length();
    }
    
    const areaInMm2 = totalArea * 1000000; // convert to square mm
    
    onAddMeasurement({
      id: Date.now(),
      type: 'area',
      value: areaInMm2,
      points: measurementPoints.map(p => p.toArray()),
      timestamp: Date.now()
    });
    setMeasurementPoints([]);
  }, [measurementPoints, onAddMeasurement]);

  const handleCompletePerimeter = useCallback(() => {
    if (measurementPoints.length < 2) return;
    
    let totalLength = 0;
    for (let i = 0; i < measurementPoints.length - 1; i++) {
      totalLength += measurementPoints[i].distanceTo(measurementPoints[i + 1]);
    }
    if (measurementPoints.length >= 3) {
      totalLength += measurementPoints[measurementPoints.length - 1].distanceTo(measurementPoints[0]);
    }
    
    const lengthInMm = totalLength * 1000; // to mm
    
    onAddMeasurement({
      id: Date.now(),
      type: 'perimeter',
      value: lengthInMm,
      points: measurementPoints.map(p => p.toArray()),
      timestamp: Date.now()
    });
    setMeasurementPoints([]);
  }, [measurementPoints, onAddMeasurement]);

  useImperativeHandle(ref, () => ({
    resetCamera: () => {
      if (controlsRef.current && controlsRef.current.customReset) {
        controlsRef.current.customReset();
      } else {
        controlsRef.current?.reset();
      }
    },
    resetMaterials: () => {
      if (controlsRef.current?.object) {
        let root = controlsRef.current.object.parent;
        while (root && root.parent) {
          root = root.parent;
        }
        if (root) {
          root.traverse((node) => {
            if (node.isMesh && node.userData?.originalMaterial) {
              node.material = node.userData.originalMaterial;
              if (node.material && node.material.userData?.originalValues) {
                const ov = node.material.userData.originalValues;
                if (ov.transparent !== undefined) node.material.transparent = ov.transparent;
                if (ov.opacity !== undefined) node.material.opacity = ov.opacity;
                if (ov.depthWrite !== undefined) node.material.depthWrite = ov.depthWrite;
                if (ov.wireframe !== undefined) node.material.wireframe = ov.wireframe;
                node.material.needsUpdate = true;
              }
            }
          });
        }
      }
    }
  }));

  return (
    <div 
      className={`relative w-full h-full outline-none ${bgClass || 'bg-gradient-to-br from-background to-muted/20'}`}
      tabIndex={0} // Symmetrical: Make the div focusable so it captures keyboard events flawlessly!
      onKeyDown={(e) => {
        // Symmetrical Keyboard Gizmo Sizing Shortcuts: maya/blender standard [ and ] keys!
        if (e.key === '[') {
          onGizmoSizeChange(Math.max(0.1, gizmoSize - 0.1)); // Shrink handles instantly!
        } else if (e.key === ']') {
          onGizmoSizeChange(Math.min(2.0, gizmoSize + 0.1)); // Grow handles instantly!
        }
      }}
    >
      <Canvas 
        onPointerMissed={(e) => {
          // Symmetrical Pointer Missed Protection: Only unselect if the click actually originated on the CANVAS element itself!
          // This completely prevents clicks on HTML sidebar buttons or tabs from leaking and unselecting the model.
          if (e && e.target && e.target.tagName === 'CANVAS') {
            if (onComponentClick) {
              onComponentClick(null);
            }
          }
          setCanvasDraggable(false); // Reset drag readiness cleanly on missing clicks!
        }}
        draggable={allowDragToInvoice ? canvasDraggable : undefined}
        onDragStart={allowDragToInvoice ? (e) => {
          if (selectedComponent?.name) {
            e.stopPropagation();
            e.dataTransfer.setData('text/plain', selectedComponent.name);
            
            // Symmetrical Custom Ghost Drag Image:
            // Custom, separate component tag preview representing only the selected mesh!
            const dragIcon = document.createElement('div');
            dragIcon.textContent = `⚙️ Component: ${selectedComponent.name}`;
            dragIcon.style.position = 'absolute';
            dragIcon.style.top = '-1000px';
            dragIcon.style.background = '#4f46e5';
            dragIcon.style.color = '#ffffff';
            dragIcon.style.padding = '6px 12px';
            dragIcon.style.borderRadius = '8px';
            dragIcon.style.fontSize = '10px';
            dragIcon.style.fontWeight = 'black';
            dragIcon.style.textTransform = 'uppercase';
            dragIcon.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.3)';
            dragIcon.style.border = '1px solid #6366f1';
            document.body.appendChild(dragIcon);
            
            e.dataTransfer.setDragImage(dragIcon, 15, 15);
            setTimeout(() => {
              document.body.removeChild(dragIcon);
            }, 0);
          }
        } : undefined}
        onDragEnd={allowDragToInvoice ? () => {
          setCanvasDraggable(false);
        } : undefined}
                gl={{ 
          preserveDrawingBuffer: true, 
          toneMapping: THREE.ACESFilmicToneMapping, 
          toneMappingExposure: 1.15,
          antialias: !isMini 
        }} 
        dpr={isInteracting ? 0.55 : [1, 2]}
        shadows={!isMini} 
        camera={{ position: cameraPosition, fov: 50 }}
      >
        <ambientLight intensity={((props.lighting?.ambientOcclusion ?? 40) / 100) * 0.4} />
        <directionalLight 
          position={[10, 10, 5]} 
          intensity={((props.lighting?.intensity ?? 75) / 100) * 1.2} 
          color={kelvinToColor(props.lighting?.temperature ?? 5500)}
          castShadow={!isMini && props.lighting?.shadows !== false} 
          shadow-mapSize-width={1024} 
          shadow-mapSize-height={1024} 
        />
        {/* Opposite Fill Light and Under Light to illuminate shadow sides evenly and prevent dark spots */}
        <directionalLight 
          position={[-10, 5, -5]} 
          intensity={((props.lighting?.intensity ?? 75) / 100) * 0.4} 
          color={kelvinToColor(props.lighting?.temperature ?? 5500)}
          castShadow={false}
        />
        <directionalLight 
          position={[0, -10, 0]} 
          intensity={((props.lighting?.intensity ?? 75) / 100) * 0.2} 
          color={kelvinToColor(props.lighting?.temperature ?? 5500)}
          castShadow={false}
        />
        <Suspense fallback={null}>
          <Environment
            preset={activeEnvPreset}
            background={props.lighting?.showEnvironmentBackground}
            backgroundBlurriness={0.5}
          />
        </Suspense>
        {props.showGrid && <Grid renderOrder={-1} position={[0, floorY - 0.01, 0]} infiniteGrid />}
        <ContactShadows 
          position={[0, floorY, 0]} 
          opacity={!isMini && props.lighting?.shadows !== false ? ((props.lighting?.shadowSoftness ?? 50) / 100) * 0.6 : 0} 
          scale={25} 
          blur={1.5} 
          far={4} 
          resolution={512} 
          frames={1} 
        />
        <Suspense fallback={<LoadingSpinner />}>
          {modelUrl && modelUrl !== 'null' && !modelUrl.endsWith('/null') ? (
            <LoadedModel 
              modelUrl={modelUrl} 
              isChunked={isChunked}
              controlsRef={controlsRef}
              setCameraPosition={setCameraPosition}
              setFloorY={setFloorY}
              isMini={isMini}
              activeMeasurementTool={activeMeasurementTool}
              measurements={measurements}
              measurementPoints={measurementPoints}
              onAddMeasurementPoint={handleAddMeasurementPoint}
              onCompleteArea={handleCompleteArea}
              onCompletePerimeter={handleCompletePerimeter}
              selectedMeasurementId={selectedMeasurementId}
              selectedPointKey={selectedPointKey}
              onSelectPointKey={setSelectedPointKey}
              unit={unit}
              chunkProgress={chunkProgress}
              setChunkProgress={setChunkProgress}
              onComponentDoubleClick={onComponentDoubleClick}
              onContextMenuRequest={onContextMenuRequest}
              componentWraps={componentWraps}
              selectedComponent={selectedComponent}
              onComponentClick={onComponentClick}
              setCanvasDraggable={setCanvasDraggable}
              allowDragToInvoice={allowDragToInvoice}
              modelYShift={modelYShift}
              isMultiSelectActive={isMultiSelectActive}
              selectedComponentIds={selectedComponentIds}
              onToggleOrbit={setOrbitEnabled} // Symmetrical: Pass OrbitControls state setter down!
              onUpdateComponentWrap={onUpdateComponentWrap} // Symmetrical: Pass coordinate update callback down!
              gizmoSize={gizmoSize} // Symmetrical: Pass active gizmo size scale factor down!
              onGizmoSizeChange={onGizmoSizeChange} // Symmetrical: Pass active gizmo size updater down!
              repairGuideOffsets={repairGuideOffsets}
              repairBuilderActive={repairBuilderActive}
              onRepairPartMoved={onRepairPartMoved}
              activeStepIndex={activeStepIndex}
              activeStepCamera={activeStepCamera}
              showStringLines={showStringLines}
              activeStepActions={activeStepActions}
              {...props} 
            />
          ) : null}
        </Suspense>

        {isInfiniteVertical ? (
          <TrackballControls 
            ref={controlsRef} 
            noZoom={false} 
            noPan={false} 
            staticMoving={false} 
            dynamicDampingFactor={0.04}
            zoomSpeed={0.75}
            minDistance={0.25}
            maxDistance={35}
          />
        ) : (
          <OrbitControls 
            ref={controlsRef} 
            makeDefault 
            enableDamping 
            dampingFactor={0.04} 
            zoomSpeed={0.75}
            minDistance={0.25}
            maxDistance={35}
            autoRotate={autoRotate} 
            autoRotateSpeed={autoRotateSpeed} 
            target={isMini ? [0, 0.45, 0] : [0, 0, 0]}
            onStart={() => setIsInteracting(true)}
            onEnd={() => setIsInteracting(false)}
          /> // Symmetrical: Set as default camera controller so useThree holds it!
        )}
      </Canvas>

      {/* Symmetrical Floating WebGL Live Coordinates Debug Overlay */}
      {selectedComponent && componentWraps[selectedComponent.id] && (
        <div 
          style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            zIndex: 9999,
            backgroundColor: 'rgba(9, 13, 22, 0.95)',
            border: '2px solid #6366f1',
            padding: '12px',
            borderRadius: '10px',
            color: '#34d399',
            fontFamily: 'monospace',
            fontSize: '11px',
            lineHeight: '1.5',
            pointerEvents: 'none',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
            minWidth: '220px'
          }}
        >
          <div style={{ color: '#6366f1', fontWeight: 'black', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.05em' }}>
            🛰️ SWAP COORDINATES DETECTED
          </div>
          <div style={{ borderBottom: '1px solid #334155', pb: '4px', mb: '6px' }}>
            Part: <span style={{ color: '#ffffff', fontWeight: 'bold' }}>{selectedComponent.id}</span>
          </div>
          <div>X Pos: <span style={{ color: '#38bdf8' }}>{(componentWraps[selectedComponent.id]?.position?.[0] ?? 0).toFixed(5)}</span></div>
          <div>Y Pos: <span style={{ color: '#38bdf8' }}>{(componentWraps[selectedComponent.id]?.position?.[1] ?? 0).toFixed(5)}</span></div>
          <div>Z Pos: <span style={{ color: '#38bdf8' }}>{(componentWraps[selectedComponent.id]?.position?.[2] ?? 0).toFixed(5)}</span></div>
          <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '8px', borderTop: '1px solid #334155', paddingTop: '4px' }}>
            * Final proportional coordinates parsed successfully.
          </div>
        </div>
      )}
    </div>
  );
});

export default React.memo(ViewerCanvas);
